import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from './lib/axiosClient';
import { useTenant } from './contexts/TenantContext';
import Import from './Import';

const C = {
  bg: '#0f0f11', panel: '#16161a', border: '#2a2a35', text: '#f0f0f2',
  muted: '#7a7a8a', dim: '#4a4a5a', amber: '#f5a623', green: '#3ecf8e',
  blue: '#4f8ef7', red: '#e8484a', purple: '#a78bfa', orange: '#f97316',
};

const mono = { fontFamily: "'DM Mono', monospace" };
const pill = (color, bg) => ({ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px', background: bg, color });
const card = (alert) => ({ background: C.panel, border: `1px solid ${alert ? 'rgba(232,72,74,0.25)' : C.border}`, borderRadius: '12px', padding: '18px 20px' });
const panelStyle = { background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' };
const ph = { padding: '13px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const row = { display: 'flex', alignItems: 'center', padding: '10px 18px', borderBottom: `1px solid #1f1f26`, gap: '12px' };
const inputStyle = { background: '#1e1e24', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 12px', color: C.text, fontFamily: 'inherit', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' };
const btnPrimary = { background: C.amber, color: '#000', border: 'none', borderRadius: '8px', padding: '7px 16px', fontWeight: 700, fontSize: '12.5px', cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 16px', fontWeight: 600, fontSize: '12.5px', cursor: 'pointer' };
const btnDanger = { background: 'rgba(232,72,74,0.12)', color: C.red, border: `1px solid rgba(232,72,74,0.25)`, borderRadius: '8px', padding: '7px 16px', fontWeight: 600, fontSize: '12.5px', cursor: 'pointer' };

// ── Modal Overlay ──────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, width = '520px' }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '14px', width, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: '15px' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '20px' }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '5px' }}>{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ ...inputStyle, ...props.style }} />;
}

function Select({ children, ...props }) {
  return <select {...props} style={{ ...inputStyle, ...props.style }}>{children}</select>;
}

function Textarea(props) {
  return <textarea {...props} style={{ ...inputStyle, minHeight: '70px', resize: 'vertical', ...props.style }} />;
}

const StatCard = ({ label, value, sub, color, alert }) => (
  <div style={card(alert)}>
    <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: C.muted, marginBottom: '8px' }}>{label}</div>
    <div style={{ fontWeight: 700, fontSize: '26px', lineHeight: 1, marginBottom: '5px', color: color || C.text }}>{value}</div>
    {sub && <div style={{ fontSize: '11px', color: C.muted }}>{sub}</div>}
  </div>
);

const TABS = [
  { id: 'dashboard', label: '📊 Dashboard' },
  { id: 'pos', label: '🛒 New Sale' },
  { id: 'sales', label: '🧾 Transactions' },
  { id: 'inventory', label: '📦 Inventory' },
  { id: 'customers', label: '👥 Customers' },
  { id: 'vendors', label: '🚚 Vendors' },
  { id: 'contacts', label: '🤝 Contacts' },
  { id: 'crm', label: '🤝 B2B Pipeline' },
  { id: 'finance', label: '💰 Finance' },
  { id: 'purchase_orders', label: '📋 Purchase Orders' },
  { id: 'staff', label: '👥 Staff & Commissions' },
  { id: 'import', label: '📥 Import CSV' },
];


// ══════════════════════════════════════════════════════
// FINANCE TAB
// ══════════════════════════════════════════════════════
function FinanceTab() {
  
  const { tenant } = useTenant();
  const tid = tenant?.id;

  const EXPENSE_CATS = [
    { id: 'rent', label: '🏢 Rent / Lease' },
    { id: 'utilities', label: '💡 Utilities' },
    { id: 'insurance', label: '🛡️ Insurance' },
    { id: 'payroll', label: '👥 Payroll' },
    { id: 'vendor_payment', label: '📦 Vendor Payment' },
    { id: 'inventory_purchase', label: '🛒 Inventory Purchase' },
    { id: 'supplies', label: '🧹 Supplies' },
    { id: 'marketing', label: '📣 Marketing / Ads' },
    { id: 'software', label: '💻 Software / Tools' },
    { id: 'maintenance', label: '🔧 Maintenance / Repairs' },
    { id: 'shipping', label: '🚚 Shipping / Delivery' },
    { id: 'banking', label: '🏦 Banking / Fees' },
    { id: 'taxes', label: '📋 Taxes / Licenses' },
    { id: 'other', label: '📎 Other' },
  ];

  const INCOME_CATS = [
    { id: 'sales', label: '🛍️ Product Sales' },
    { id: 'service', label: '💼 Service Income' },
    { id: 'tips', label: '💵 Tips' },
    { id: 'rental', label: '🏠 Rental Income' },
    { id: 'refund_received', label: '↩️ Refund Received' },
    { id: 'other_income', label: '📎 Other Income' },
  ];

  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [form, setForm] = React.useState({ type: 'expense', category: 'vendor_payment', amount: '', description: '', date: new Date().toISOString().split('T')[0], notes: '' });
  const [filter, setFilter] = React.useState('month');
  const [viewType, setViewType] = React.useState('pl');

  React.useEffect(function() { if (tid) load(); }, [tid]);

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get('/api/smokeshop/finance?tenant_id=' + tid);
      setEntries(r.data.entries || []);
    } catch(e) { setEntries([]); }
    setLoading(false);
  }

  async function save() {
    if (!form.amount || !form.date) return;
    try {
      await axios.post('/api/smokeshop/finance', { ...form, tenant_id: tid, amount: parseFloat(form.amount) });
      setShowModal(false); setForm({ type: 'expense', category: 'vendor_payment', amount: '', description: '', date: new Date().toISOString().split('T')[0], notes: '' }); load();
    } catch(e) {}
  }

  async function del(id) {
    if (!window.confirm('Delete this entry?')) return;
    await axios.delete('/api/smokeshop/finance/' + id);
    load();
  }

  function filtered() {
    const now = new Date();
    return entries.filter(function(e) {
      const d = new Date(e.date);
      if (filter === 'week') { const w = new Date(now); w.setDate(now.getDate()-7); return d >= w; }
      if (filter === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      if (filter === 'year') return d.getFullYear() === now.getFullYear();
      return true;
    });
  }

  const rows = filtered();
  const totalIn = rows.filter(function(e) { return e.type === 'income'; }).reduce(function(s,e) { return s+parseFloat(e.amount||0); }, 0);
  const totalOut = rows.filter(function(e) { return e.type === 'expense'; }).reduce(function(s,e) { return s+parseFloat(e.amount||0); }, 0);
  const net = totalIn - totalOut;

  function groupBy(type) {
    const g = {};
    rows.filter(function(e) { return e.type === type; }).forEach(function(e) { g[e.category] = (g[e.category]||0) + parseFloat(e.amount||0); });
    return g;
  }

  const expG = groupBy('expense');
  const incG = groupBy('income');
  
  const card = { background: C.card, border: '1px solid '+C.border, borderRadius: 14, padding: 20, marginBottom: 16 };
  const lbl = { fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: C.muted, marginBottom: 6 };
  const inp = { width: '100%', padding: '10px 12px', background: C.bg, border: '1px solid '+C.border, borderRadius: 8, color: C.text, fontSize: 14, boxSizing: 'border-box' };

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:C.text }}>💰 Money In / Money Out</div>
          <div style={{ fontSize:13, color:C.muted, marginTop:2 }}>Full chart of accounts — track every dollar</div>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button onClick={async function(){
            if (!window.confirm('Sync all historical sales as income entries? This may create duplicates if already synced.')) return;
            try {
              const resp = await axios.post('/api/smokeshop/finance/sync-sales');
              const d = resp.data;
              alert('Synced ' + (d.synced || 0) + ' sales to finance!');
              load();
            } catch(e) { alert('Sync failed: ' + e.message); }
          }} style={{ padding:'8px 14px', background:'#6366f1', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>
            🔄 Sync History
          </button>
          <button onClick={function(){ setForm(function(f){ return {...f, type:'income', category:'sales'}; }); setShowModal(true); }}
            style={{ padding:'8px 16px', background:'#10b981', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>+ Income</button>
          <button onClick={function(){ setForm(function(f){ return {...f, type:'expense', category:'vendor_payment'}; }); setShowModal(true); }}
            style={{ padding:'8px 16px', background:'#ef4444', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>+ Expense</button>
        </div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
        {[['week','This Week'],['month','This Month'],['year','This Year'],['all','All Time']].map(function(f){ return (
          <button key={f[0]} onClick={function(){ setFilter(f[0]); }}
            style={{ padding:'6px 14px', background: filter===f[0] ? C.amber : C.card, color: filter===f[0] ? '#000' : C.muted, border:'1px solid '+(filter===f[0] ? C.amber : C.border), borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer' }}>{f[1]}</button>
        ); })}
        <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
          {[['pl','P&L'],['entries','Entries']].map(function(v){ return (
            <button key={v[0]} onClick={function(){ setViewType(v[0]); }}
              style={{ padding:'6px 12px', background: viewType===v[0] ? C.card : 'transparent', color: viewType===v[0] ? C.text : C.muted, border:'1px solid '+(viewType===v[0] ? C.border : 'transparent'), borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer' }}>{v[1]}</button>
          ); })}
        </div>
      </div>

      {viewType === 'pl' ? (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div style={card}>
            <div style={{ ...lbl, color:'#10b981', marginBottom:14 }}>📥 Income</div>
            {Object.keys(incG).length === 0 ? <div style={{ color:C.muted, fontSize:13, textAlign:'center', padding:20 }}>No income entries</div> :
              Object.entries(incG).sort(function(a,b){ return b[1]-a[1]; }).map(function([cat,amt]){
                const c = INCOME_CATS.find(function(x){ return x.id===cat; }) || { label: cat };
                return (
                  <div key={cat} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid '+C.border }}>
                    <span style={{ fontSize:13, color:C.text }}>{c.label}</span>
                    <span style={{ fontSize:14, fontWeight:700, color:'#10b981' }}>${amt.toFixed(2)}</span>
                  </div>
                );
              })
            }
            <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10 }}>
              <span style={{ fontWeight:700, fontSize:13, color:C.text }}>Total</span>
              <span style={{ fontWeight:900, fontSize:14, color:'#10b981' }}>${totalIn.toFixed(2)}</span>
            </div>
          </div>

          <div style={card}>
            <div style={{ ...lbl, color:'#ef4444', marginBottom:14 }}>📤 Expenses</div>
            {Object.keys(expG).length === 0 ? <div style={{ color:C.muted, fontSize:13, textAlign:'center', padding:20 }}>No expense entries</div> :
              Object.entries(expG).sort(function(a,b){ return b[1]-a[1]; }).map(function([cat,amt]){
                const c = EXPENSE_CATS.find(function(x){ return x.id===cat; }) || { label: cat };
                const pct = totalOut > 0 ? (amt/totalOut*100).toFixed(0) : 0;
                return (
                  <div key={cat} style={{ padding:'8px 0', borderBottom:'1px solid '+C.border }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                      <span style={{ fontSize:13, color:C.text }}>{c.label}</span>
                      <span style={{ fontSize:14, fontWeight:700, color:'#ef4444' }}>${amt.toFixed(2)}</span>
                    </div>
                    <div style={{ height:3, background:C.border, borderRadius:2 }}>
                      <div style={{ height:3, background:'#ef4444', borderRadius:2, width:pct+'%', opacity:0.6 }} />
                    </div>
                  </div>
                );
              })
            }
            <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10 }}>
              <span style={{ fontWeight:700, fontSize:13, color:C.text }}>Total</span>
              <span style={{ fontWeight:900, fontSize:14, color:'#ef4444' }}>${totalOut.toFixed(2)}</span>
            </div>
          </div>
        </div>
      ) : (
        <div style={card}>
          <div style={lbl}>All Entries</div>
          {loading ? <div style={{ color:C.muted, textAlign:'center', padding:30 }}>Loading...</div> :
           rows.length === 0 ? <div style={{ color:C.muted, textAlign:'center', padding:30 }}>No entries for this period</div> :
           [...rows].sort(function(a,b){ return new Date(b.date)-new Date(a.date); }).map(function(e){
             const isIn = e.type === 'income';
             const cats = isIn ? INCOME_CATS : EXPENSE_CATS;
             const cat = cats.find(function(c){ return c.id===e.category; }) || { label: e.category };
             return (
               <div key={e.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid '+C.border }}>
                 <div style={{ width:36, height:36, borderRadius:10, background: isIn?'#10b98120':'#ef444420', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
                   {isIn ? '📥' : '📤'}
                 </div>
                 <div style={{ flex:1, minWidth:0 }}>
                   <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{e.description || cat.label}</div>
                   <div style={{ fontSize:12, color:C.muted }}>{cat.label} · {new Date(e.date).toLocaleDateString()}</div>
                 </div>
                 <div style={{ fontSize:16, fontWeight:800, color: isIn?'#10b981':'#ef4444', flexShrink:0 }}>
                   {isIn?'+':'-'}${parseFloat(e.amount).toFixed(2)}
                 </div>
                 <button onClick={function(){ del(e.id); }} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:16, padding:4 }}>🗑️</button>
               </div>
             );
           })}
        </div>
      )}

      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:16, padding:24, width:'100%', maxWidth:440 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:18, fontWeight:800, color:C.text }}>{form.type==='income' ? '📥 Add Income' : '📤 Add Expense'}</div>
              <button onClick={function(){ setShowModal(false); }} style={{ background:'none', border:'none', color:C.muted, fontSize:22, cursor:'pointer' }}>✕</button>
            </div>

            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {[['income','📥 Income'],['expense','📤 Expense']].map(function(t){ return (
                <button key={t[0]} onClick={function(){ setForm(function(f){ return {...f, type:t[0], category: t[0]==='income'?'sales':'vendor_payment'}; }); }}
                  style={{ flex:1, padding:'8px', background: form.type===t[0] ? (t[0]==='income'?'#10b981':'#ef4444') : C.bg, color: form.type===t[0]?'#fff':C.muted, border:'1px solid '+C.border, borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
                  {t[1]}
                </button>
              ); })}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div>
                <div style={lbl}>Amount *</div>
                <input type="number" step="0.01" placeholder="0.00" value={form.amount}
                  onChange={function(e){ setForm(function(f){ return {...f, amount:e.target.value}; }); }} style={inp} />
              </div>
              <div>
                <div style={lbl}>Date *</div>
                <input type="date" value={form.date}
                  onChange={function(e){ setForm(function(f){ return {...f, date:e.target.value}; }); }} style={inp} />
              </div>
            </div>

            <div style={{ marginBottom:12 }}>
              <div style={lbl}>Category *</div>
              <select value={form.category} onChange={function(e){ setForm(function(f){ return {...f, category:e.target.value}; }); }} style={inp}>
                {(form.type==='income' ? INCOME_CATS : EXPENSE_CATS).map(function(c){ return (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ); })}
              </select>
            </div>

            <div style={{ marginBottom:12 }}>
              <div style={lbl}>Description</div>
              <input type="text" placeholder="e.g. Delta 8 restock — ABC Distributors" value={form.description}
                onChange={function(e){ setForm(function(f){ return {...f, description:e.target.value}; }); }} style={inp} />
            </div>

            <div style={{ marginBottom:20 }}>
              <div style={lbl}>Notes</div>
              <textarea placeholder="Optional notes..." value={form.notes}
                onChange={function(e){ setForm(function(f){ return {...f, notes:e.target.value}; }); }}
                style={{ ...inp, minHeight:60, resize:'vertical' }} />
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={function(){ setShowModal(false); }}
                style={{ flex:1, padding:'12px', background:C.bg, color:C.muted, border:'1px solid '+C.border, borderRadius:10, fontWeight:700, cursor:'pointer' }}>Cancel</button>
              <button onClick={save}
                style={{ flex:2, padding:'12px', background: form.type==='income'?'#10b981':'#ef4444', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14 }}>
                Save {form.type==='income'?'Income':'Expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════
// PURCHASE ORDERS TAB
// ══════════════════════════════════════════════════════
function PurchaseOrdersTab({ reorderItem, onReorderConsumed }) {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ vendor_id: '', vendor_name: '', order_date: new Date().toISOString().split('T')[0], expected_date: '', notes: '', items: [] });

  // Pre-fill PO form when coming from low stock reorder
  useEffect(() => {
    if (reorderItem) {
      setForm(f => ({
        ...f,
        items: [{
          product_id: reorderItem.id,
          product_name: reorderItem.name,
          sku: reorderItem.sku || '',
          quantity: reorderItem.reorder_quantity || reorderItem.low_stock_threshold || 10,
          cost_per_unit: reorderItem.cost_price || reorderItem.cost || ''
        }]
      }));
      setShowModal(true);
      onReorderConsumed && onReorderConsumed();
    }
  }, [reorderItem]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get('/api/purchase-orders'),
      axios.get('/api/smokeshop-products'),
      axios.get('/api/vendors-list'),
    ]).then(([poRes, prodRes, vendRes]) => {
      setOrders(poRes.data.orders || []);
      setProducts(prodRes.data.products || []);
      setVendors(vendRes.data.vendors || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { product_id: '', product_name: '', sku: '', quantity: 1, cost_per_unit: '' }] }));
  const setItem = (i, k, v) => setForm(f => { const items = [...f.items]; items[i] = { ...items[i], [k]: v }; return { ...f, items }; });
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const selectProduct = (i, productId) => {
    const p = products.find(p => p.id === productId);
    if (p) setForm(f => { const items = [...f.items]; items[i] = { ...items[i], product_id: p.id, product_name: p.name, sku: p.sku || '', cost_per_unit: p.cost_price || p.cost || '' }; return { ...f, items }; });
  };

  const totalCost = form.items.reduce((s, i) => s + (parseFloat(i.cost_per_unit || 0) * parseInt(i.quantity || 1)), 0);

  const savePO = async () => {
    if (!form.vendor_name && !form.vendor_id) return alert('Select a vendor');
    if (!form.items.length) return alert('Add at least one item');
    try {
      const vendor = vendors.find(v => v.id === form.vendor_id);
      await axios.post('/api/purchase-orders', { ...form, vendor_name: vendor?.company_name || form.vendor_name });
      setShowModal(false);
      setForm({ vendor_id: '', vendor_name: '', order_date: new Date().toISOString().split('T')[0], expected_date: '', notes: '', items: [] });
      load();
    } catch(e) { alert('Error: ' + e.message); }
  };

  const updateStatus = async (id, status) => {
    const label = { ordered: 'Mark as Ordered', received: 'Mark as Received', cancelled: 'Cancel' }[status];
    if (!window.confirm(label + '? ' + (status === 'received' ? 'This will update inventory and log the expense.' : ''))) return;
    try {
      await axios.put(`/api/purchase-orders/${id}`, { status });
      load();
    } catch(e) { alert('Error: ' + e.message); }
  };

  const statusColor = { draft: C.muted, ordered: C.amber, received: C.green, cancelled: C.red };
  const statusLabel = { draft: '📝 Draft', ordered: '📦 Ordered', received: '✅ Received', cancelled: '❌ Cancelled' };

  const totalSpend = orders.filter(o => o.status === 'received').reduce((s, o) => s + parseFloat(o.total_cost || 0), 0);
  const pending = orders.filter(o => o.status === 'ordered').length;

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>📋 Purchase Orders</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>Restock inventory · auto-updates stock + finance</div>
        </div>
        <button onClick={() => setShowModal(true)} style={btnPrimary}>+ New PO</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 24 }}>
        {[['Total Spent', '$'+totalSpend.toLocaleString('en-US',{minimumFractionDigits:2}), C.amber],['Pending Orders', pending, C.green],['Total POs', orders.length, C.muted]].map(s => (
          <div key={s[0]} style={{ background: C.card, border: '1px solid '+C.border, borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.muted, marginBottom: 6 }}>{s[0]}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s[2] }}>{s[1]}</div>
          </div>
        ))}
      </div>

      {loading ? <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading...</div> :
       orders.length === 0 ? <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>No purchase orders yet. Create one to restock inventory.</div> :
       orders.map(o => (
        <div key={o.id} style={{ background: C.card, border: '1px solid '+C.border, borderRadius: 14, padding: 20, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{o.vendor_name || 'Unknown Vendor'}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                Ordered: {o.order_date || '-'} 
                {o.expected_date ? ' · Expected: '+o.expected_date : ''}
                {o.received_date ? ' · Received: '+o.received_date : ''}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: statusColor[o.status], background: statusColor[o.status]+'20', borderRadius: 8, padding: '4px 10px' }}>{statusLabel[o.status]}</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: C.amber }}>${parseFloat(o.total_cost||0).toFixed(2)}</span>
            </div>
          </div>

          {(o.purchase_order_items || []).length > 0 && (
            <div style={{ background: C.bg, borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>Items</div>
              {o.purchase_order_items.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid '+C.border, fontSize: 13 }}>
                  <span style={{ color: C.text }}>{item.product_name} <span style={{ color: C.muted }}>× {item.quantity}</span></span>
                  <span style={{ color: C.muted }}>${parseFloat(item.cost_per_unit||0).toFixed(2)}/ea = <span style={{ color: C.amber, fontWeight: 700 }}>${parseFloat(item.total_cost||0).toFixed(2)}</span></span>
                </div>
              ))}
            </div>
          )}

          {o.notes && <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>📝 {o.notes}</div>}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {o.status === 'draft' && <button onClick={() => updateStatus(o.id, 'ordered')} style={{ padding: '6px 14px', background: C.amber, color: '#000', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>📦 Mark Ordered</button>}
            {o.status === 'ordered' && <button onClick={() => updateStatus(o.id, 'received')} style={{ padding: '6px 14px', background: C.green, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>✅ Mark Received</button>}
            {(o.status === 'draft' || o.status === 'ordered') && <button onClick={() => updateStatus(o.id, 'cancelled')} style={{ padding: '6px 14px', background: 'transparent', color: C.red, border: '1px solid '+C.red, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Cancel</button>}
          </div>
        </div>
       ))
      }

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: C.card, border: '1px solid '+C.border, borderRadius: 16, padding: 24, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>📋 New Purchase Order</div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>Vendor *</div>
                <select value={form.vendor_id} onChange={e => setForm(f => ({ ...f, vendor_id: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', background: C.bg, border: '1px solid '+C.border, borderRadius: 8, color: C.text, fontSize: 14 }}>
                  <option value="">Select vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>Order Date</div>
                <input type="date" value={form.order_date} onChange={e => setForm(f => ({ ...f, order_date: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', background: C.bg, border: '1px solid '+C.border, borderRadius: 8, color: C.text, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>Expected Delivery</div>
                <input type="date" value={form.expected_date} onChange={e => setForm(f => ({ ...f, expected_date: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', background: C.bg, border: '1px solid '+C.border, borderRadius: 8, color: C.text, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>Notes</div>
                <input type="text" placeholder="Optional notes..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', background: C.bg, border: '1px solid '+C.border, borderRadius: 8, color: C.text, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Items</div>
                <button onClick={addItem} style={{ padding: '5px 12px', background: C.amber, color: '#000', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>+ Add Item</button>
              </div>
              {form.items.length === 0 && <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 16 }}>No items yet — click Add Item</div>}
              {form.items.map((item, i) => (
                <div key={i} style={{ background: C.bg, borderRadius: 10, padding: 12, marginBottom: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                    <select value={item.product_id} onChange={e => selectProduct(i, e.target.value)}
                      style={{ padding: '8px 10px', background: C.card, border: '1px solid '+C.border, borderRadius: 8, color: C.text, fontSize: 13 }}>
                      <option value="">Select product</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <input type="number" placeholder="Qty" min="1" value={item.quantity} onChange={e => setItem(i,'quantity',e.target.value)}
                      style={{ padding: '8px 10px', background: C.card, border: '1px solid '+C.border, borderRadius: 8, color: C.text, fontSize: 13 }} />
                    <input type="number" placeholder="$/unit" step="0.01" value={item.cost_per_unit} onChange={e => setItem(i,'cost_per_unit',e.target.value)}
                      style={{ padding: '8px 10px', background: C.card, border: '1px solid '+C.border, borderRadius: 8, color: C.text, fontSize: 13 }} />
                    <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: C.red, fontSize: 18, cursor: 'pointer', padding: 4 }}>✕</button>
                  </div>
                  {item.product_name && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{item.product_name} · Subtotal: ${(parseFloat(item.cost_per_unit||0)*parseInt(item.quantity||1)).toFixed(2)}</div>}
                </div>
              ))}
            </div>

            {form.items.length > 0 && (
              <div style={{ background: C.bg, borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, color: C.text }}>Total Cost</span>
                <span style={{ fontWeight: 900, fontSize: 18, color: C.amber }}>${totalCost.toFixed(2)}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '12px', background: C.bg, color: C.muted, border: '1px solid '+C.border, borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={savePO} style={{ flex: 2, padding: '12px', background: C.amber, color: '#000', border: 'none', borderRadius: 10, fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>Save Purchase Order</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════
// REVENUE CHART COMPONENT
// ══════════════════════════════════════════════════════
function RevenueChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxRevenue = Math.max(...data.map(d => d.revenue), 1);
  const width = 100;
  const height = 60;
  const padding = 4;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;
  const points = data.map((d, i) => {
    const x = padding + (i / (data.length - 1)) * chartWidth;
    const y = padding + (1 - d.revenue / maxRevenue) * chartHeight;
    return [x, y];
  });
  const pathD = points.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
  const areaD = pathD + ' L' + points[points.length-1][0].toFixed(1) + ',' + (height-padding) + ' L' + padding + ',' + (height-padding) + ' Z';
  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
  const activeDays = data.filter(d => d.revenue > 0).length;
  const best = data.reduce((best, d) => d.revenue > best.revenue ? d : best, data[0]);

  return (
    <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 16, padding: 24, marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.muted, marginBottom: 4 }}>Revenue — Last 30 Days</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: C.amber }}>${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
        </div>
        <div style={{ display: 'flex', gap: 20 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Active Days</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{activeDays}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Best Day</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.green }}>${best.revenue.toFixed(0)}</div>
            <div style={{ fontSize: 10, color: C.muted }}>{new Date(best.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
          </div>
        </div>
      </div>
      <svg viewBox={'0 0 ' + width + ' ' + height} style={{ width: '100%', height: 120, overflow: 'visible' }} preserveAspectRatio="none">
        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f5a623" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#f5a623" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#chartGrad)" />
        <path d={pathD} fill="none" stroke="#f5a623" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => data[i].revenue > 0 && (
          <circle key={i} cx={p[0]} cy={p[1]} r="0.8" fill="#f5a623" />
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 10, color: C.muted }}>{new Date(data[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        <span style={{ fontSize: 10, color: C.muted }}>Today</span>
      </div>
    </div>
  );
}

export default function SmokeshopDashboard({ defaultTab }) {
  const [activeTab, setActiveTab] = useState(defaultTab || 'dashboard');
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewSale, setShowNewSale] = useState(false);
  // Listen for custom event from sidebar New Sale button
  useEffect(() => {
    const handler = () => setActiveTab('pos');
    window.addEventListener('openSmokeshopPOS', handler);
    return () => window.removeEventListener('openSmokeshopPOS', handler);
  }, []);
  const [reorderItem, setReorderItem] = useState(null); // pre-fill PO from low stock

  const loadDash = useCallback(() => {
    axios.get('/api/smokeshop/dashboard')
      .then(r => { setDashData(r.data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { loadDash(); }, [loadDash]);

  const tabBar = (
    <div style={{ display: 'flex', gap: '4px', padding: '10px 20px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap', alignItems: 'center', background: C.panel }}>
      {TABS.map(t => (
        <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
          padding: '6px 13px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
          color: activeTab === t.id ? C.amber : C.muted,
          background: activeTab === t.id ? 'rgba(245,166,35,0.12)' : 'transparent',
          border: activeTab === t.id ? '1px solid rgba(245,166,35,0.3)' : '1px solid transparent',
          transition: 'all 0.15s',
        }}>{t.label}</button>
      ))}
      <button onClick={() => setActiveTab('pos')} style={{ ...btnPrimary, marginLeft: 'auto', fontSize: '12px', padding: '6px 14px' }}>+ New Sale</button>
    </div>
  );

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: C.amber }}>Loading...</div>;

  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text, fontFamily: "'DM Sans',-apple-system,sans-serif", fontSize: '14px' }}>
      {tabBar}
      <div style={{ padding: '20px 24px' }}>
        {activeTab === 'dashboard' && <DashTab data={dashData} error={error} goTo={setActiveTab} onReorder={function(item){ setReorderItem(item); setActiveTab('purchase_orders'); }} />}
        {activeTab === 'pos' && <POSPage onBack={() => setActiveTab('sales')} onSaved={() => { setActiveTab('sales'); loadDash(); }} />}
        {activeTab === 'sales' && <SalesTab onNewSale={() => setActiveTab('pos')} />}
        {activeTab === 'inventory' && <InventoryTab />}
        {activeTab === 'customers' && <CustomersTab />}
        {activeTab === 'vendors' && <VendorsTab />}
        {activeTab === 'contacts' && <ContactsTab />}
        {activeTab === 'crm' && <CRMTab />}
        {activeTab === 'finance' && <FinanceTab />}
        {activeTab === 'purchase_orders' && <PurchaseOrdersTab reorderItem={reorderItem} onReorderConsumed={function(){ setReorderItem(null); }} />}
        {activeTab === 'staff' && <StaffCommissionsTab />}
        {activeTab === 'import' && <ImportTab />}
      </div>

    </div>
  );
}

// ── Dashboard Tab ──────────────────────────────────────────────────────────────
function DashTab({ data, error, goTo, onReorder }) {
  const { today, monthly, low_stock, low_stock_count, recent_sales, top_customers, daily_revenue, staff_leaderboard, payment_breakdown } = data || {};
  const [detailModal, setDetailModal] = React.useState(null); // 'today' | 'avg' | 'monthly' | 'lowstock'
  const colors = ['#f5a623', '#4f8ef7', '#3ecf8e', '#a78bfa', '#e8484a', '#f97316'];
  if (error) return <div style={{ padding: '40px', textAlign: 'center', color: C.red }}>Failed to load: {error}</div>;
  return (
    <>
      <RevenueChart data={daily_revenue} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '20px' }}>
        {[
          { key:'today', label:"Today's Revenue", value:`$${parseFloat(today?.revenue||0).toLocaleString('en-US',{minimumFractionDigits:2})}`, sub:`${today?.transaction_count||0} transactions today`, color:C.amber },
          { key:'avg', label:'Avg Ticket', value:`$${today?.avg_ticket||'0.00'}`, sub:`Cash: ${today?.cash_count||0} · Card: ${today?.card_count||0}`, color:C.text },
          { key:'monthly', label:'Monthly Revenue', value:`$${parseFloat(monthly?.revenue||0).toLocaleString('en-US',{minimumFractionDigits:2})}`, sub:`${monthly?.transaction_count||0} transactions this month`, color:C.green },
          { key:'lowstock', label:'Low Stock Alerts', value:low_stock_count||0, sub:'Items need reorder', color:C.red },
        ].map(function(card){ return (
          <div key={card.key} onClick={function(){ setDetailModal(card.key); }}
            style={{ background:C.card, border:'1px solid '+C.border, borderRadius:14, padding:'18px 20px', cursor:'pointer', transition:'border-color .15s', position:'relative' }}
            onMouseEnter={function(e){ e.currentTarget.style.borderColor=C.amber; }}
            onMouseLeave={function(e){ e.currentTarget.style.borderColor=C.border; }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:C.muted, marginBottom:6 }}>{card.label}</div>
            <div style={{ fontWeight:700, fontSize:26, lineHeight:1, marginBottom:5, color:card.color }}>{card.value}</div>
            <div style={{ fontSize:11, color:C.muted }}>{card.sub}</div>
            <div style={{ position:'absolute', top:12, right:14, fontSize:10, color:C.muted, opacity:0.5 }}>tap for details</div>
          </div>
        ); })}
      </div>

      {/* Detail Modals */}
      {detailModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }} onClick={function(){ setDetailModal(null); }}>
          <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:16, padding:24, width:'100%', maxWidth:480, maxHeight:'80vh', overflowY:'auto' }} onClick={function(e){ e.stopPropagation(); }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:18, fontWeight:800, color:C.text }}>
                {detailModal==='today' && "📊 Today's Revenue"}
                {detailModal==='avg' && '🎯 Average Ticket'}
                {detailModal==='monthly' && '📈 Monthly Revenue'}
                {detailModal==='lowstock' && '⚠️ Low Stock Items'}
              </div>
              <button onClick={function(){ setDetailModal(null); }} style={{ background:'none', border:'none', color:C.muted, fontSize:22, cursor:'pointer' }}>✕</button>
            </div>

            {detailModal==='today' && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                  {[['Total Revenue','$'+parseFloat(today?.revenue||0).toFixed(2),C.amber],['Transactions',today?.transaction_count||0,C.text],['Cash Sales',today?.cash_count||0,C.green],['Card Sales',today?.card_count||0,'#818cf8']].map(function(s){ return (
                    <div key={s[0]} style={{ background:C.bg, borderRadius:10, padding:14 }}>
                      <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>{s[0]}</div>
                      <div style={{ fontSize:20, fontWeight:800, color:s[2] }}>{s[1]}</div>
                    </div>
                  ); })}
                </div>
                <div style={{ fontSize:12, color:C.muted, marginBottom:8, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em' }}>Recent Sales Today</div>
                {(recent_sales||[]).slice(0,5).map(function(s,i){ return (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid '+C.border, fontSize:13 }}>
                    <span style={{ color:C.text }}>{s.customer_name||'Walk-in'} <span style={{ color:C.muted, fontSize:11 }}>· {s.payment_method}</span></span>
                    <span style={{ color:C.amber, fontWeight:700 }}>${parseFloat(s.total||0).toFixed(2)}</span>
                  </div>
                ); })}
              </div>
            )}

            {detailModal==='avg' && (
              <div>
                <div style={{ background:C.bg, borderRadius:12, padding:20, marginBottom:16, textAlign:'center' }}>
                  <div style={{ fontSize:48, fontWeight:900, color:C.amber }}>${today?.avg_ticket||'0.00'}</div>
                  <div style={{ color:C.muted, fontSize:13, marginTop:4 }}>Average per transaction today</div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {[['Cash Transactions',today?.cash_count||0,C.green],['Card Transactions',today?.card_count||0,'#818cf8'],['Total Transactions',today?.transaction_count||0,C.amber],['Total Revenue','$'+parseFloat(today?.revenue||0).toFixed(2),C.text]].map(function(s){ return (
                    <div key={s[0]} style={{ background:C.bg, borderRadius:10, padding:14 }}>
                      <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>{s[0]}</div>
                      <div style={{ fontSize:20, fontWeight:800, color:s[2] }}>{s[1]}</div>
                    </div>
                  ); })}
                </div>
              </div>
            )}

            {detailModal==='monthly' && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                  {[['Monthly Revenue','$'+parseFloat(monthly?.revenue||0).toLocaleString('en-US',{minimumFractionDigits:2}),C.green],['Transactions',monthly?.transaction_count||0,C.text],["Today's Revenue",'$'+parseFloat(today?.revenue||0).toFixed(2),C.amber],['Avg Ticket','$'+(today?.avg_ticket||'0.00'),C.muted]].map(function(s){ return (
                    <div key={s[0]} style={{ background:C.bg, borderRadius:10, padding:14 }}>
                      <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>{s[0]}</div>
                      <div style={{ fontSize:20, fontWeight:800, color:s[2] }}>{s[1]}</div>
                    </div>
                  ); })}
                </div>
                <div style={{ background:C.bg, borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>Daily Average</div>
                  <div style={{ fontSize:24, fontWeight:800, color:C.green }}>
                    ${monthly?.transaction_count ? (parseFloat(monthly?.revenue||0) / new Date().getDate()).toFixed(2) : '0.00'}
                  </div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>per day this month</div>
                </div>
              </div>
            )}

            {detailModal==='lowstock' && (
              <div>
                {(low_stock||[]).length===0 ? (
                  <div style={{ textAlign:'center', padding:30, color:C.green, fontSize:16 }}>✅ All items in stock!</div>
                ) : (low_stock||[]).map(function(item,i){ 
                  const crit = item.current_stock===0 || item.current_stock <= Math.floor((item.low_stock_threshold||5)*0.5);
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid '+C.border }}>
                      <div style={{ width:10, height:10, borderRadius:'50%', background: crit?C.red:C.amber, flexShrink:0 }} />
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{item.name}</div>
                        <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>SKU: {item.sku||'-'} · Reorder qty: {item.reorder_quantity||10}</div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontSize:16, fontWeight:800, color:crit?C.red:C.amber }}>{item.current_stock} left</div>
                      <button onClick={function(){ setDetailModal(null); onReorder && onReorder(item); }} style={{ marginTop:4, padding:'3px 10px', background:C.amber, color:'#000', border:'none', borderRadius:6, fontWeight:700, fontSize:11, cursor:'pointer' }}>Reorder</button>
                        <div style={{ fontSize:11, color:C.muted }}>min: {item.low_stock_threshold||5}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '18px', marginBottom: '18px' }}>
        <div style={panelStyle}>
          <div style={ph}><span style={{ fontWeight: 700, fontSize: '13px' }}>Recent Transactions</span><button onClick={() => goTo('sales')} style={{ fontSize: '11.5px', color: C.amber, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>All Sales →</button></div>
          {!(recent_sales || []).length && <div style={{ padding: '30px', textAlign: 'center', color: C.dim }}>No sales yet today</div>}
          {[...new Map((recent_sales || []).map(s => [s.id, s])).values()].slice(0, 10).map((s, i) => (
            <div key={i} style={row}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>{s.customer_name || 'Walk-in'}</div>
                <div style={{ ...mono, fontSize: '11px', color: C.muted, marginTop: '2px' }}>{new Date(s.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}{s.staff_name ? ` · ${s.staff_name}` : ''}</div>
              </div>
              <span style={pill(s.payment_method === 'cash' ? C.green : C.blue, s.payment_method === 'cash' ? 'rgba(62,207,142,.1)' : 'rgba(79,142,247,.1)')}>{(s.payment_method || 'cash').toUpperCase()}</span>
              <div style={{ ...mono, fontSize: '13px', color: C.green, fontWeight: 600, minWidth: '60px', textAlign: 'right' }}>${parseFloat(s.total || 0).toFixed(2)}</div>
            </div>
          ))}
        </div>
      <div style={panelStyle}>
          <div style={ph}><span style={{ fontWeight: 700, fontSize: '13px' }}>Low Stock</span><button onClick={() => goTo('inventory')} style={{ fontSize: '11.5px', color: C.amber, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View All →</button></div>
          {!(low_stock || []).length && <div style={{ padding: '30px', textAlign: 'center', color: C.dim }}>All items in stock ✓</div>}
          {[...new Map((low_stock || []).map(item => [item.id, item])).values()].map((item, i) => {
            const crit = item.current_stock === 0 || item.current_stock <= Math.floor((item.low_stock_threshold || 5) * 0.5);
            return (
              <div key={i} style={row}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: crit ? C.red : C.amber, flexShrink: 0 }} />
                <div style={{ flex: 1 }}><div style={{ fontSize: '12.5px' }}>{item.name}</div><div style={{ ...mono, fontSize: '10px', color: C.dim }}>{item.sku}</div></div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ ...mono, fontSize: '13px', fontWeight: 700, color: crit ? C.red : C.amber }}>{item.current_stock} left</div>
                  <button onClick={function(e){ e.stopPropagation(); onReorder && onReorder(item); }} style={{ padding:'2px 8px', background:C.amber, color:'#000', border:'none', borderRadius:6, fontWeight:700, fontSize:11, cursor:'pointer' }}>Reorder</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

        {/* Staff Leaderboard */}
        {(staff_leaderboard || []).length > 0 && (
          <div style={{ ...panelStyle, marginTop: 18 }}>
            <div style={ph}><span style={{ fontWeight: 700, fontSize: '13px' }}>🏆 Staff This Month</span></div>
            {staff_leaderboard.map((s, i) => (
              <div key={i} style={row}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: i===0?'#f5a623':i===1?'#aaa':i===2?'#cd7f32':C.border, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800, color: i<3?'#000':C.muted, flexShrink:0 }}>{i+1}</div>
                <div style={{ flex: 1 }}><div style={{ fontSize: '13px', fontWeight: 600 }}>{s.name}</div><div style={{ fontSize: '11px', color: C.muted }}>{s.count} transactions</div></div>
                <div style={{ ...mono, fontSize: '13px', fontWeight: 700, color: C.amber }}>${s.total.toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      <div style={{ ...panelStyle, maxWidth: '580px' }}>
        <div style={ph}><span style={{ fontWeight: 700, fontSize: '13px' }}>Top Customers</span><button onClick={() => goTo('customers')} style={{ fontSize: '11.5px', color: C.amber, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>All Customers →</button></div>
        {!(top_customers || []).length && <div style={{ padding: '30px', textAlign: 'center', color: C.dim }}>No customer data yet</div>}
        {(top_customers || []).map((c, i) => {
          const initials = `${(c.first_name || '')[0] || ''}${(c.last_name || '')[0] || ''}`.toUpperCase();
          return (
            <div key={i} style={row}>
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: colors[i % colors.length] + '22', border: `1px solid ${colors[i % colors.length]}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '11px', color: colors[i % colors.length], flexShrink: 0 }}>{initials}</div>
              <div style={{ flex: 1 }}><div style={{ fontSize: '13px', fontWeight: 500 }}>{c.first_name} {c.last_name}</div><div style={{ fontSize: '11px', color: C.muted }}>{c.total_visits || 0} visits</div></div>
              <div style={{ ...mono, fontSize: '13px', color: C.amber, fontWeight: 600 }}>${parseFloat(c.total_spent || 0).toFixed(2)}</div>
              {c.is_vip && <span style={pill(C.amber, 'rgba(245,166,35,.12)')}>VIP</span>}
            </div>
          );
        })}
      </div>
    </>
  );
}


// ── New Sale Modal — Upgraded POS ─────────────────────────────────────────────
function NewSaleModal({ onClose, onSaved }) {
  const [receipt, setReceipt] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [cart, setCart] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [posMembership, setPosMembership] = useState(null);
  const [memberDiscountApplied, setMemberDiscountApplied] = useState(false);
  const [showMemberDiscountPrompt, setShowMemberDiscountPrompt] = useState(false);
  const [managerCode, setManagerCode] = useState('');
  const [showCustDrop, setShowCustDrop] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashGiven, setCashGiven] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountType, setDiscountType] = useState('$');
  const [discountLocked, setDiscountLocked] = useState(true);
  const [showDiscountLock, setShowDiscountLock] = useState(false);
  const [discountManagerCode, setDiscountManagerCode] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [savingCust, setSavingCust] = useState(false);

  const categories = ['All', 'Vapes', 'Glass', 'CBD', 'Papers', 'Hookah', 'Accessories', 'Misc'];

  useEffect(() => {
    axios.get('/api/smokeshop-products').then(r => setProducts(r.data.products || [])).catch(() => {});
    axios.get('/api/customers?limit=300').then(r => setCustomers(r.data.customers || r.data || [])).catch(() => {});
    axios.get('/api/staff').then(r => setStaff(r.data.staff || [])).catch(() => {});
  }, []);

  const filteredProducts = products.filter(p => {
    const ms = !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.sku || '').toLowerCase().includes(productSearch.toLowerCase());
    const mc = !categoryFilter || categoryFilter === 'All' || p.category === categoryFilter;
    return ms && mc;
  });

  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return true;
    const q = customerSearch.toLowerCase();
    return (c.first_name + ' ' + (c.last_name || '') + ' ' + (c.phone || '')).toLowerCase().includes(q);
  }).slice(0, 6);

  const addToCart = (p) => {
    setCart(prev => {
      const ex = prev.find(i => i.product_id === p.id);
      if (ex) return prev.map(i => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { product_id: p.id, name: p.name, price: parseFloat(p.retail_price || 0), cost: parseFloat(p.cost || 0), qty: 1, sku: p.sku, category: p.category }];
    });
  };

  const updateQty = (id, qty) => {
    if (qty < 1) { setCart(prev => prev.filter(i => i.product_id !== id)); return; }
    setCart(prev => prev.map(i => i.product_id === id ? { ...i, qty } : i));
  };

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const tax = subtotal * 0.0825;
  const discAmt = discountType === '$' ? parseFloat(discount || 0) : subtotal * parseFloat(discount || 0) / 100;
  const total = Math.max(0, subtotal + tax - discAmt);
  const change = paymentMethod === 'cash' ? Math.max(0, parseFloat(cashGiven || 0) - total) : 0;

  const handleSave = async () => {
    if (!cart.length) { setError('Add at least one item'); return; }
    setSaving(true); setError('');
    try {
      const { data } = await axios.post('/api/smokeshop-sales', {
        contact_id: selectedCustomer?.id || null,
        customer_name: selectedCustomer ? selectedCustomer.first_name + ' ' + (selectedCustomer.last_name || '') : 'Walk-in',
        staff_name: selectedStaff || null,
        payment_method: paymentMethod,
        line_items: cart,
        subtotal: subtotal.toFixed(2),
        tax: tax.toFixed(2),
        discount: discAmt.toFixed(2),
        total: total.toFixed(2),
        notes,
      });
      setReceipt(data.sale || data);
    } catch (e) { setError(e.response?.data?.detail || e.message); setSaving(false); }
  };

  const handleSaveNewCust = async () => {
    if (!newCust.first_name) return;
    setSavingCust(true);
    try {
      const { data } = await axios.post('/api/customers', newCust);
      const created = data.customer || data;
      setCustomers(prev => [created, ...prev]);
      setSelectedCustomer(created);
      setShowNewCust(false);
      setNewCust({ first_name: '', last_name: '', phone: '', email: '' });
    } catch(e) {}
    setSavingCust(false);
  };

  // ── RECEIPT ──────────────────────────────────────────────────────────────────
  if (receipt) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 20, padding: 32, width: '100%', maxWidth: 420, textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 8 }}>✅</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: C.green, marginBottom: 4 }}>Sale Complete!</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 24, fontFamily: 'monospace' }}>#{receipt.receipt_number || String(receipt.id || '').slice(-6).toUpperCase()}</div>
        <div style={{ background: C.bg, border: '1px solid ' + C.border, borderRadius: 14, padding: 20, marginBottom: 20, textAlign: 'left' }}>
          {[
            ['Customer', selectedCustomer ? selectedCustomer.first_name + ' ' + (selectedCustomer.last_name || '') : 'Walk-in'],
            ['Staff', selectedStaff || '—'],
            ['Payment', paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1)],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
              <span style={{ color: C.muted }}>{k}</span>
              <span style={{ fontWeight: 600, color: C.text, textTransform: 'capitalize' }}>{v}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px dashed ' + C.border, margin: '12px 0' }} />
          {cart.map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: C.text }}>{item.name} <span style={{ color: C.muted }}>×{item.qty}</span></span>
              <span style={{ color: C.text }}>${(item.price * item.qty).toFixed(2)}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px dashed ' + C.border, margin: '12px 0' }} />
          {[['Subtotal', '$' + subtotal.toFixed(2)], ['Tax (8.25%)', '$' + tax.toFixed(2)], discAmt > 0 && ['Discount', '-$' + discAmt.toFixed(2)]].filter(Boolean).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 4 }}>
              <span>{k}</span><span>{v}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid ' + C.border, marginTop: 6 }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: C.text }}>TOTAL</span>
            <span style={{ fontSize: 22, fontWeight: 900, color: C.amber }}>${total.toFixed(2)}</span>
          </div>
          {paymentMethod === 'cash' && parseFloat(cashGiven) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, padding: '8px 0', borderTop: '1px solid ' + C.border }}>
              <span style={{ fontSize: 14, color: C.muted }}>Change Due</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: C.green }}>${change.toFixed(2)}</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => window.print()} style={{ flex: 1, padding: 14, background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>🖨️ Print</button>
          <button onClick={onSaved} style={{ flex: 2, padding: 14, background: C.amber, color: '#000', border: 'none', borderRadius: 12, fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>New Sale →</button>
        </div>
      </div>
    </div>
  );

  // ── POS LAYOUT ───────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{ padding: '10px 20px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: C.panel }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid ' + C.border, color: C.muted, cursor: 'pointer', fontSize: 13, padding: '5px 12px', borderRadius: 8, fontWeight: 600 }}>← Back</button>
        <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>🛒 New Sale</span>
        <select value={selectedCustomer ? selectedCustomer.id : ''} onChange={e => {
            const cust = customers.find(c => String(c.id) === e.target.value);
                        const cust2 = cust || null;
            setSelectedCustomer(cust2);
            setPosMembership(null); setMemberDiscountApplied(false);
            if (cust2) {
              axios.get('/api/memberships/subscriptions').then(r => {
                const subs = r.data.subscriptions || [];
                const sub = subs.find(s => String(s.contact_id) === String(cust2.id) && s.status === 'active');
                setPosMembership(sub ? sub.membership_plans : null);
              }).catch(() => {});
            }
          }}
          style={{ flex: 1, maxWidth: 260, padding: '7px 12px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 10, color: C.text, fontSize: 13, outline: 'none' }}>
          <option value="">👤 Walk-in</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.is_vip ? '⭐' : ''}</option>)}
        </select>
        <button onClick={() => setShowNewCust(true)} style={{ padding: '7px 12px', background: C.green, color: '#000', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ New Customer</button>
        <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)}
          style={{ maxWidth: 180, padding: '7px 12px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 10, color: C.text, fontSize: 13, outline: 'none' }}>
          <option value="">👤 No Staff</option>
          {staff.map(s => <option key={s.id} value={s.name || s.first_name}>{s.name || s.first_name + ' ' + (s.last_name || '')}</option>)}
        </select>
      </div>

      {/* Membership alert */}
      {posMembership && (
        <div style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>👑</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa' }}>{posMembership.name} Member</div>
            {posMembership.perks && <div style={{ fontSize: 11, color: '#7a7a8a', marginTop: 1 }}>{posMembership.perks}</div>}
          </div>
          {memberDiscountApplied
            ? <span style={{ fontSize: 11, fontWeight: 700, color: '#3ecf8e', padding: '3px 10px', background: 'rgba(62,207,142,0.1)', borderRadius: 6 }}>✅ Discount Applied</span>
            : <button onClick={() => setShowMemberDiscountPrompt(true)} style={{ padding: '6px 14px', background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 8, color: '#a78bfa', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Apply Discount</button>
          }
        </div>
      )}

      {/* Manager approval prompt */}
      {showMemberDiscountPrompt && (
        <div style={{ background: '#1a1a22', border: '1px solid #2a2a35', borderRadius: 12, padding: '16px', marginTop: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#f0f0f2', marginBottom: 8 }}>🔐 Manager Approval Required</div>
          <div style={{ fontSize: 12, color: '#7a7a8a', marginBottom: 10 }}>Enter manager code to apply member discount</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="password" value={managerCode} onChange={e => setManagerCode(e.target.value)}
              placeholder="Manager code..." style={{ flex: 1, padding: '8px 12px', background: '#0f0f11', border: '1px solid #2a2a35', borderRadius: 8, color: '#f0f0f2', fontSize: 13, outline: 'none' }}
              onKeyDown={e => { if (e.key === 'Enter') { if (managerCode === '1234') { setMemberDiscountApplied(true); setShowMemberDiscountPrompt(false); setManagerCode(''); } else { alert('Invalid code'); setManagerCode(''); } }}} />
            <button onClick={() => { if (managerCode === '1234') { setMemberDiscountApplied(true); setShowMemberDiscountPrompt(false); setManagerCode(''); } else { alert('Invalid code'); setManagerCode(''); }}}
              style={{ padding: '8px 16px', background: '#f5a623', color: '#000', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer', fontSize: 13 }}>Approve</button>
            <button onClick={() => { setShowMemberDiscountPrompt(false); setManagerCode(''); }}
              style={{ padding: '8px 12px', background: 'transparent', border: '1px solid #2a2a35', borderRadius: 8, color: '#7a7a8a', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Discount unlock modal */}
      {showDiscountLock && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#16161a', border: '1px solid #2a2a35', borderRadius: 14, padding: 24, width: 320 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#f0f0f2', marginBottom: 6 }}>🔐 Manager Approval</div>
            <div style={{ fontSize: 12, color: '#7a7a8a', marginBottom: 14 }}>Enter manager code to unlock discounts</div>
            <input type="password" value={discountManagerCode} onChange={e => setDiscountManagerCode(e.target.value)} autoFocus
              placeholder="Manager code..." style={{ width: '100%', padding: '9px 12px', background: '#0f0f11', border: '1px solid #2a2a35', borderRadius: 8, color: '#f0f0f2', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
              onKeyDown={e => { if (e.key === 'Enter') { if (discountManagerCode === '1234') { setDiscountLocked(false); setShowDiscountLock(false); setDiscountManagerCode(''); } else { alert('Invalid code'); setDiscountManagerCode(''); }}}} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setShowDiscountLock(false); setDiscountManagerCode(''); }} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid #2a2a35', borderRadius: 8, color: '#7a7a8a', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={() => { if (discountManagerCode === '1234') { setDiscountLocked(false); setShowDiscountLock(false); setDiscountManagerCode(''); } else { alert('Invalid code'); setDiscountManagerCode(''); }}}
                style={{ flex: 2, padding: 10, background: '#f5a623', color: '#000', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>Unlock</button>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* LEFT: Products */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid ' + C.border, overflow: 'hidden' }}>
          {/* Search + categories */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + C.border, flexShrink: 0 }}>
            <input value={productSearch} onChange={e => setProductSearch(e.target.value)}
              placeholder="🔍 Search products or scan SKU..."
              style={{ width: '100%', padding: '10px 14px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 10, color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 10, color: C.text, fontSize: 13, outline: 'none' }}>
              {categories.map(cat => <option key={cat} value={cat === 'All' ? '' : cat}>{cat}</option>)}
            </select>
          </div>
          {/* Product grid */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, alignContent: 'start' }}>
            {filteredProducts.map(p => {
              const inCart = cart.find(i => i.product_id === p.id);
              const outOfStock = !p.current_stock || p.current_stock <= 0;
              const catColors = { Vapes: '#6366f1', Glass: '#06b6d4', CBD: '#10b981', Papers: '#f97316', Hookah: '#8b5cf6', Accessories: '#ec4899', Misc: '#64748b' };
              return (
                <div key={p.id} onClick={() => !outOfStock && addToCart(p)}
                  style={{ background: inCart ? C.amber + '15' : C.card, border: '2px solid ' + (inCart ? C.amber : C.border),
                    borderRadius: 14, padding: '16px 18px', cursor: outOfStock ? 'not-allowed' : 'pointer', opacity: outOfStock ? 0.4 : 1,
                    transition: 'border-color .1s', position: 'relative' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: catColors[p.category] || C.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>{p.category || 'Misc'}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6, lineHeight: 1.3 }}>{p.name}</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: inCart ? C.amber : C.text }}>${parseFloat(p.retail_price || 0).toFixed(2)}</div>
                  <div style={{ fontSize: 10, color: outOfStock ? C.red : p.current_stock <= 5 ? C.amber : C.muted, marginTop: 4 }}>
                    {outOfStock ? 'Out of stock' : p.current_stock + ' in stock'}
                  </div>
                  {inCart && <div style={{ position: 'absolute', top: 8, right: 10, background: C.amber, color: '#000', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900 }}>{inCart.qty}</div>}
                </div>
              );
            })}
            {filteredProducts.length === 0 && <div style={{ color: C.muted, textAlign: 'center', padding: 40, gridColumn: '1/-1' }}>No products found</div>}
          </div>
        </div>

        {/* RIGHT: Cart */}
        <div style={{ width: 300, minWidth: 300, display: 'flex', flexDirection: 'column', background: C.panel, flexShrink: 0 }}>
          {/* Cart items */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
            {cart.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🛒</div>
                <div style={{ fontSize: 13 }}>Tap products to add</div>
              </div>
            ) : cart.map((item, i) => (
              <div key={i} style={{ padding: '10px 16px', borderBottom: '1px solid ' + C.border }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1, marginRight: 8 }}>{item.name}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.amber, whiteSpace: 'nowrap' }}>${(item.price * item.qty).toFixed(2)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 11, color: C.muted }}>${item.price.toFixed(2)} each</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => updateQty(item.product_id, item.qty - 1)} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid ' + C.border, background: 'transparent', color: C.text, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>−</button>
                    <span style={{ fontSize: 15, fontWeight: 800, color: C.text, minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                    <button onClick={() => updateQty(item.product_id, item.qty + 1)} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid ' + C.border, background: 'transparent', color: C.text, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>+</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals + Payment */}
          <div style={{ borderTop: '1px solid ' + C.border, padding: '14px 16px', flexShrink: 0 }}>
            {/* Discount */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <button onClick={() => setDiscountType('$')} style={{ padding: '5px 12px', background: discountType === '$' ? C.amber : C.bg, color: discountType === '$' ? '#000' : C.muted, border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>$</button>
              <button onClick={() => setDiscountType('%')} style={{ padding: '5px 12px', background: discountType === '%' ? C.amber : C.bg, color: discountType === '%' ? '#000' : C.muted, border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>%</button>
              <div style={{ flex: 1, position: 'relative' }}>
                <input type="number" value={discount} onChange={e => !discountLocked && setDiscount(e.target.value)}
                  placeholder={discountLocked ? '🔒 Locked' : 'Discount ' + discountType}
                  readOnly={discountLocked}
                  style={{ width: '100%', padding: '5px 10px', background: discountLocked ? '#111' : '#1e1e24', border: '1px solid ' + (discountLocked ? C.border : C.amber), borderRadius: 8, color: discountLocked ? C.muted : C.text, fontSize: 13, outline: 'none', boxSizing: 'border-box', cursor: discountLocked ? 'not-allowed' : 'text' }} />
              </div>
              <button onClick={() => discountLocked ? setShowDiscountLock(true) : (setDiscountLocked(true), setDiscount(''))}
                style={{ padding: '5px 10px', background: discountLocked ? 'rgba(232,72,74,0.1)' : 'rgba(62,207,142,0.1)', border: '1px solid ' + (discountLocked ? 'rgba(232,72,74,0.2)' : 'rgba(62,207,142,0.2)'), borderRadius: 8, color: discountLocked ? '#e8484a' : '#3ecf8e', cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {discountLocked ? '🔒' : '🔓 Lock'}
              </button>
            </div>

            {/* Totals */}
            <div style={{ marginBottom: 12 }}>
              {[['Subtotal', '$' + subtotal.toFixed(2)], ['Tax (8.25%)', '$' + tax.toFixed(2)], discAmt > 0 && ['Discount', '-$' + discAmt.toFixed(2)]].filter(Boolean).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 4 }}>
                  <span>{k}</span><span>{v}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid ' + C.border, marginTop: 6 }}>
                <span style={{ fontSize: 20, fontWeight: 900, color: C.text }}>Total</span>
                <span style={{ fontSize: 24, fontWeight: 900, color: C.amber }}>${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment method */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
              {[['cash', '💵'], ['card', '💳'], ['venmo', '📱'], ['zelle', '⚡']].map(([key, icon]) => (
                <button key={key} onClick={() => setPaymentMethod(key)}
                  style={{ padding: '8px', background: paymentMethod === key ? C.amber : C.bg, color: paymentMethod === key ? '#000' : C.muted,
                    border: '1px solid ' + (paymentMethod === key ? C.amber : C.border), borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13, textTransform: 'capitalize' }}>
                  {icon} {key}
                </button>
              ))}
            </div>

            {/* Cash change calculator */}
            {paymentMethod === 'cash' && (
              <div style={{ marginBottom: 10 }}>
                <input type="number" value={cashGiven} onChange={e => setCashGiven(e.target.value)} placeholder="Cash tendered..."
                  style={{ width: '100%', padding: '8px 12px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 10, color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 6 }} />
                {parseFloat(cashGiven) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: C.green + '15', borderRadius: 10, border: '1px solid ' + C.green }}>
                    <span style={{ color: C.green, fontWeight: 700, fontSize: 14 }}>Change Due</span>
                    <span style={{ color: C.green, fontWeight: 900, fontSize: 18 }}>${change.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            {error && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>⚠ {error}</div>}
            <button onClick={handleSave} disabled={saving || cart.length === 0}
              style={{ width: '100%', padding: 16, background: cart.length ? C.amber : C.border, color: cart.length ? '#000' : C.muted,
                border: 'none', borderRadius: 14, fontWeight: 900, cursor: cart.length ? 'pointer' : 'not-allowed', fontSize: 16, transition: 'background .15s' }}>
              {saving ? 'Processing...' : cart.length ? 'Charge $' + total.toFixed(2) : 'Add Items to Charge'}
            </button>
          </div>
        </div>
      </div>
    {showNewCust && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 16, padding: 28, width: 400, maxWidth: '95vw' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 20 }}>👤 New Customer</div>
          {[['First Name *', 'first_name'], ['Last Name', 'last_name'], ['Phone', 'phone'], ['Email', 'email']].map(([label, key]) => (
            <div key={key} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
              <input value={newCust[key]} onChange={e => setNewCust(p => ({ ...p, [key]: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setShowNewCust(false)} style={{ flex: 1, padding: 12, background: 'transparent', color: C.muted, border: '1px solid ' + C.border, borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSaveNewCust} disabled={savingCust || !newCust.first_name} style={{ flex: 2, padding: 12, background: C.amber, color: '#000', border: 'none', borderRadius: 10, fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>{savingCust ? 'Saving...' : 'Add Customer'}</button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}


// ── POSPage wrapper
function POSPage({ onBack, onSaved }) {
  return <NewSaleModal onClose={onBack} onSaved={onSaved} />;
}


// ── Sales Tab ──────────────────────────────────────────────────────────────────
function SalesTab({ onNewSale }) {
  const [sales, setSales] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState('');

  const load = useCallback((d) => {
    setLoading(true);
    axios.get(`/api/smokeshop-sales?limit=100${d ? '&date=' + d : ''}`)
      .then(r => { setSales(r.data.sales || []); setToday(r.data.today); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(''); }, [load]);

  const cols = { display: 'grid', gridTemplateColumns: '90px 1fr 130px 70px 80px 90px', padding: '8px 18px', borderBottom: `1px solid ${C.border}` };
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '20px' }}>
        <StatCard label="Today's Revenue" value={`$${parseFloat(today?.revenue || 0).toFixed(2)}`} color={C.amber} />
        <StatCard label="Transactions" value={today?.transaction_count || 0} />
        <StatCard label="Cash" value={today?.cash_count || 0} color={C.green} />
        <StatCard label="Card" value={today?.card_count || 0} color={C.blue} />
      </div>
      <div style={panelStyle}>
        <div style={{ ...ph, gap: '10px' }}>
          <span style={{ fontWeight: 700, fontSize: '13px' }}>Transactions</span>
          <input type="date" value={date} onChange={e => { setDate(e.target.value); load(e.target.value); }} style={{ ...inputStyle, width: '160px', fontSize: '12px' }} />
          {date && <button onClick={() => { setDate(''); load(''); }} style={{ fontSize: '11px', color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>}
          <button style={{ ...btnPrimary, marginLeft: 'auto' }} onClick={onNewSale}>+ New Sale</button>
        </div>
        <div style={cols}>{['Time', 'Items', 'Customer', 'Method', 'Total', 'Staff'].map(h => <span key={h} style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: C.dim }}>{h}</span>)}</div>
        {loading && <div style={{ padding: '30px', textAlign: 'center', color: C.muted }}>Loading...</div>}
        {!loading && !sales.length && <div style={{ padding: '30px', textAlign: 'center', color: C.dim }}>No transactions found</div>}
        {sales.map((s, i) => {
          const items = Array.isArray(s.line_items) ? s.line_items.map(x => x.name).join(', ') : 'Sale';
          return (
            <div key={i} style={{ ...cols, alignItems: 'center', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)', borderBottom: `1px solid #1f1f26` }}>
              <div style={{ ...mono, fontSize: '11.5px', color: C.muted }}>{new Date(s.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
              <div style={{ fontSize: '12px', paddingRight: '10px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{items}</div>
              <div style={{ fontSize: '12.5px' }}>{s.customer_name || 'Walk-in'}</div>
              <span style={pill(s.payment_method === 'cash' ? C.green : C.blue, s.payment_method === 'cash' ? 'rgba(62,207,142,.1)' : 'rgba(79,142,247,.1)')}>{(s.payment_method || 'cash').toUpperCase()}</span>
              <div style={{ ...mono, fontSize: '13px', color: C.green, fontWeight: 600 }}>${parseFloat(s.total || 0).toFixed(2)}</div>
              <div style={{ fontSize: '12px', color: C.muted }}>{s.staff_name || '—'}</div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Inventory Tab ──────────────────────────────────────────────────────────────
function InventoryTab() {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const urlCat = new URLSearchParams(window.location.search).get('cat') || 'all';
  const [cat, setCat] = useState(urlCat);
  const [modal, setModal] = useState(null); // null | 'add' | product object

  const load = useCallback(() => {
    axios.get('/api/smokeshop-products').then(r => { setProducts(r.data.products || []); setLoading(false); }).catch(() => setLoading(false));
    axios.get('/api/vendors-list').then(r => setVendors(r.data.vendors || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const cats = ['all', ...new Set(products.map(p => p.category).filter(Boolean))];
  const filtered = products.filter(p =>
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase())) &&
    (cat === 'all' || p.category === cat)
  );

  const catColor = { Vapes: C.blue, Glass: C.green, Papers: C.amber, CBD: C.purple, Hookah: C.orange, Misc: C.muted };
  const cols = { display: 'grid', gridTemplateColumns: '70px 2fr 100px 70px 70px 70px 70px 90px 80px', padding: '8px 18px', borderBottom: `1px solid ${C.border}` };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this product?')) return;
    await axios.delete(`/api/smokeshop-products/${id}`).catch(() => {});
    load();
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '20px' }}>
      {/* Inventory Valuation */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '14px' }}>
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '16px 20px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.muted, marginBottom: 6 }}>Retail Value</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.amber }}>${products.reduce((s, p) => s + parseFloat(p.retail_price || 0) * parseInt(p.current_stock || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>If sold at full price</div>
        </div>
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '16px 20px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.muted, marginBottom: 6 }}>Cost Value</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.green }}>${products.reduce((s, p) => s + parseFloat(p.cost || 0) * parseInt(p.current_stock || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>What you paid for stock</div>
        </div>
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '16px 20px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.muted, marginBottom: 6 }}>Potential Profit</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#6366f1' }}>${(products.reduce((s, p) => s + parseFloat(p.retail_price || 0) * parseInt(p.current_stock || 0), 0) - products.reduce((s, p) => s + parseFloat(p.cost || 0) * parseInt(p.current_stock || 0), 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Gross margin on hand</div>
        </div>
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '16px 20px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.muted, marginBottom: 6 }}>Avg Margin</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.amber }}>
            {products.filter(p => p.retail_price && p.cost).length > 0
              ? Math.round(products.filter(p => p.retail_price && p.cost).reduce((s, p) => s + (1 - p.cost / p.retail_price) * 100, 0) / products.filter(p => p.retail_price && p.cost).length) + '%'
              : '—'}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Across all products</div>
        </div>
      </div>
        <StatCard label="Total SKUs" value={products.length} />
        <StatCard label="In Stock" value={products.filter(p => p.current_stock > (p.low_stock_threshold || 5)).length} color={C.green} />
        <StatCard label="Low Stock" value={products.filter(p => p.current_stock > 0 && p.current_stock <= (p.low_stock_threshold || 5)).length} color={C.amber} />
        <StatCard label="Out of Stock" value={products.filter(p => p.current_stock === 0).length} color={C.red} alert />
      </div>
      <div style={panelStyle}>
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or SKU..." style={{ ...inputStyle, maxWidth: '280px' }} />
          <select value={cat} onChange={e => setCat(e.target.value)} style={{ ...inputStyle, width: '160px', color: C.muted }}>{cats.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>)}</select>
          <button style={{ ...btnPrimary, marginLeft: 'auto' }} onClick={() => setModal('add')}>+ Add Product</button>
        </div>
        <div style={cols}>{['SKU', 'Product', 'Category', 'Cost', 'Price', 'Stock', 'Reorder', 'Status', ''].map(h => <span key={h} style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: C.dim }}>{h}</span>)}</div>
        {loading && <div style={{ padding: '30px', textAlign: 'center', color: C.muted }}>Loading...</div>}
        {filtered.map((p, i) => {
          const thresh = p.low_stock_threshold || 5;
          const out = p.current_stock === 0; const crit = !out && p.current_stock <= Math.floor(thresh * 0.5); const low = !out && !crit && p.current_stock <= thresh;
          const ac = out || crit ? C.red : low ? C.amber : C.green; const al = out ? 'Out' : crit ? 'Critical' : low ? 'Low' : 'OK';
          const cc = catColor[p.category] || C.muted;
          return (
            <div key={i} style={{ ...cols, alignItems: 'center', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)', borderBottom: `1px solid #1f1f26` }}>
              <div style={{ ...mono, fontSize: '10px', color: C.dim }}>{p.sku || '—'}</div>
              <div style={{ fontSize: '13px', fontWeight: 500, paddingRight: '8px' }}>{p.name}</div>
              <span style={{ ...pill(cc, `${cc}18`), alignSelf: 'center' }}>{p.category || '—'}</span>
              <div style={{ ...mono, fontSize: '12px', color: C.muted }}>${parseFloat(p.cost || 0).toFixed(2)}</div>
              <div style={{ ...mono, fontSize: '12px' }}>${parseFloat(p.retail_price || 0).toFixed(2)}</div>
              {p.cost_price ? <div style={{ fontSize: '10px', color: C.muted }}>Cost: ${parseFloat(p.cost_price).toFixed(2)} · Margin: {p.retail_price && p.cost ? Math.round((1 - p.cost_price/p.retail_price)*100) + '%' : '-'}</div> : null}
              <div style={{ ...mono, fontSize: '13px', fontWeight: 700, color: ac }}>{p.current_stock}</div>
              <div style={{ fontSize: '12px', color: C.dim }}>{thresh}</div>
              <span style={pill(ac, `${ac}18`)}>{al}</span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={() => setModal(p)} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '5px', background: 'rgba(79,142,247,.1)', color: C.blue, border: 'none', cursor: 'pointer' }}>Edit</button>
                <button onClick={() => handleDelete(p.id)} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '5px', background: 'rgba(232,72,74,.1)', color: C.red, border: 'none', cursor: 'pointer' }}>✕</button>
              </div>
            </div>
          );
        })}
      </div>
      {modal && <ProductModal product={modal === 'add' ? null : modal} vendors={vendors} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </>
  );
}

function ProductModal({ product, vendors, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product?.name || '', sku: product?.sku || '', category: product?.category || '',
    cost: product?.cost || '', cost_price: product?.cost_price || product?.cost || '', retail_price: product?.retail_price || '',
    current_stock: product?.current_stock ?? '', low_stock_threshold: product?.low_stock_threshold || 5,
    reorder_quantity: product?.reorder_quantity || 10,
    vendor_id: product?.vendor_id || '', description: product?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [savingCust, setSavingCust] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name) { setError('Name is required'); return; }
    setSaving(true);
    try {
      if (product) await axios.put(`/api/smokeshop-products/${product.id}`, form);
      else await axios.post('/api/smokeshop-products', form);
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || e.message); setSaving(false); }
  };

  return (
    <Modal title={product ? 'Edit Product' : 'Add Product'} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Field label="Product Name *"><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Product name" /></Field>
        <Field label="SKU"><Input value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="VAP-D8-001" /></Field>
        <Field label="Category">
          <Select value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="">Select category</option>
            {['Vapes', 'Glass', 'Papers', 'CBD', 'Hookah', 'Misc'].map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Vendor">
          <Select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}>
            <option value="">No vendor</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}
          </Select>
        </Field>
        <Field label="Cost Price ($)"><Input type="number" value={form.cost_price} onChange={e => { set('cost_price', e.target.value); set('cost', e.target.value); }} step="0.01" min="0" /></Field>
        <Field label="Retail Price ($)"><Input type="number" value={form.retail_price} onChange={e => set('retail_price', e.target.value)} step="0.01" min="0" /></Field>
        <Field label="Current Stock"><Input type="number" value={form.current_stock} onChange={e => set('current_stock', e.target.value)} min="0" /></Field>
        <Field label="Low Stock Threshold"><Input type="number" value={form.low_stock_threshold} onChange={e => set('low_stock_threshold', e.target.value)} min="0" /></Field>
        <Field label="Reorder Quantity"><Input type="number" value={form.reorder_quantity} onChange={e => set('reorder_quantity', e.target.value)} min="0" /></Field>
      </div>
      <Field label="Description"><Textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} /></Field>
      {error && <div style={{ color: C.red, fontSize: '12px', marginBottom: '10px' }}>⚠ {error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button style={btnSecondary} onClick={onClose}>Cancel</button>
        <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : product ? 'Save Changes' : 'Add Product'}</button>
      </div>
    </Modal>
  );
}

// ── Customers Tab ──────────────────────────────────────────────────────────────
function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    axios.get('/api/customers?limit=500').then(r => { setCustomers(r.data.customers || r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer? This cannot be undone.')) return;
    await axios.delete('/api/customers/' + id).catch(() => {});
    load();
  };

  const filtered = customers.filter(c => {
    if (!search) return true;
    return `${c.first_name || ''} ${c.last_name || ''} ${c.phone || ''} ${c.email || ''}`.toLowerCase().includes(search.toLowerCase());
  });

  const colors = ['#f5a623', '#4f8ef7', '#3ecf8e', '#a78bfa', '#e8484a', '#f97316', '#06b6d4', '#84cc16'];
  const cols = { display: 'grid', gridTemplateColumns: '40px 1fr 130px 70px 90px 100px 70px 60px 40px', padding: '10px 18px', borderBottom: `1px solid #1f1f26`, alignItems: 'center', gap: '12px' };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '20px' }}>
        <StatCard label="Total Customers" value={customers.length} />
        <StatCard label="VIP" value={customers.filter(c => c.is_vip).length} color={C.amber} />
        <StatCard label="Returning" value={customers.filter(c => (c.total_visits || 0) > 1).length} color={C.green} />
        <StatCard label="New" value={customers.filter(c => (c.total_visits || 0) <= 1).length} color={C.blue} />
      </div>
      <div style={panelStyle}>
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '10px' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers..." style={{ ...inputStyle, maxWidth: '300px' }} />
          <button style={{ ...btnPrimary, marginLeft: 'auto' }} onClick={() => setModal('add')}>+ Add Customer</button>
        </div>
        <div style={{ ...cols, borderBottom: `1px solid ${C.border}` }}>{['', 'Name', 'Phone', 'Visits', 'Spent', 'Last Visit', 'Status', ''].map((h, i) => <span key={i} style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: C.dim }}>{h}</span>)}</div>
        {loading && <div style={{ padding: '30px', textAlign: 'center', color: C.muted }}>Loading...</div>}
        {!loading && !filtered.length && <div style={{ padding: '30px', textAlign: 'center', color: C.dim }}>No customers found</div>}
        {filtered.map((c, i) => {
          const initials = `${(c.first_name || '')[0] || ''}${(c.last_name || '')[0] || ''}`.toUpperCase();
          const color = colors[i % colors.length];
          const status = c.is_vip ? 'VIP' : (c.total_visits || 0) > 5 ? 'Regular' : 'New';
          const sc = c.is_vip ? C.amber : (c.total_visits || 0) > 5 ? C.green : C.blue;
          return (
            <div key={i} style={{ ...cols, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: color + '22', border: `1px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '11px', color, flexShrink: 0 }}>{initials}</div>
              <div><div style={{ fontSize: '13px', fontWeight: 500 }}>{c.first_name} {c.last_name}</div><div style={{ fontSize: '11px', color: C.muted }}>{c.email || ''}</div></div>
              <div style={{ ...mono, fontSize: '12px', color: C.muted }}>{c.phone || '—'}</div>
              <div style={{ ...mono, fontSize: '13px', fontWeight: 600 }}>{c.total_visits || 0}</div>
              <div style={{ ...mono, fontSize: '13px', color: C.amber, fontWeight: 600 }}>${parseFloat(c.total_spent || 0).toFixed(2)}</div>
              <div style={{ fontSize: '12px', color: C.muted }}>{c.last_visit || '—'}</div>
              <span style={pill(sc, `${sc}18`)}>{status}</span>
              <button onClick={() => setModal(c)} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '5px', background: 'rgba(79,142,247,.1)', color: C.blue, border: 'none', cursor: 'pointer' }}>Edit</button>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: '16px', padding: '2px 6px' }} title="Delete customer">🗑</button>
            </div>
          );
        })}
      </div>
      {modal && <CustomerModal customer={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </>
  );
}

function CustomerModal({ customer, onClose, onSaved }) {
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  useEffect(() => {
    if (customer && customer.id) {
      setLoadingPurchases(true);
      axios.get('/api/smokeshop-sales?contact_id=' + customer.id)
        .then(r => { setPurchases(r.data.sales || []); setLoadingPurchases(false); })
        .catch(() => setLoadingPurchases(false));
    }
  }, [customer]);
  const [form, setForm] = useState({
    first_name: customer?.first_name || '', last_name: customer?.last_name || '',
    phone: customer?.phone || '', email: customer?.email || '',
    date_of_birth: customer?.date_of_birth || '', notes: customer?.notes || '',
    is_vip: customer?.is_vip || false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [savingCust, setSavingCust] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.first_name) { setError('First name required'); return; }
    setSaving(true);
    try {
      if (customer) await axios.put(`/api/customers/${customer.id}`, form);
      else await axios.post('/api/customers', form);
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || e.message); setSaving(false); }
  };

  return (
    <Modal title={customer ? 'Edit Customer' : 'Add Customer'} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Field label="First Name *"><Input value={form.first_name} onChange={e => set('first_name', e.target.value)} /></Field>
        <Field label="Last Name"><Input value={form.last_name} onChange={e => set('last_name', e.target.value)} /></Field>
        <Field label="Phone"><Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(702) 555-0000" /></Field>
        <Field label="Email"><Input value={form.email} onChange={e => set('email', e.target.value)} type="email" /></Field>
        <Field label="Date of Birth"><Input type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} /></Field>
        <Field label="VIP Status">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
            <input type="checkbox" id="vip" checked={form.is_vip} onChange={e => set('is_vip', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
            <label htmlFor="vip" style={{ fontSize: '13px', cursor: 'pointer' }}>Mark as VIP Customer</label>
          </div>
        </Field>
      </div>
      <Field label="Notes"><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></Field>
      {error && <div style={{ color: C.red, fontSize: '12px', marginBottom: '10px' }}>⚠ {error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
      {customer && (
        <div style={{ marginTop: 16, borderTop: '1px solid ' + C.border, paddingTop: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: C.muted, marginBottom: 10 }}>Purchase History</div>
          {loadingPurchases ? <div style={{ color: C.muted, fontSize: 13 }}>Loading...</div> :
           purchases.length === 0 ? <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 12 }}>No purchases on record</div> :
           <div style={{ maxHeight: 180, overflowY: 'auto' }}>
             {purchases.slice(0, 10).map((s, idx) => (
               <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid ' + C.border, fontSize: 13 }}>
                 <div>
                   <span style={{ color: C.text, fontWeight: 500 }}>{new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                   <span style={{ color: C.muted, fontSize: 11, marginLeft: 8 }}>{s.receipt_number}</span>
                 </div>
                 <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                   <span style={{ fontSize: 11, color: C.muted, textTransform: 'capitalize' }}>{s.payment_method}</span>
                   <span style={{ fontWeight: 700, color: C.amber }}>${parseFloat(s.total || 0).toFixed(2)}</span>
                 </div>
               </div>
             ))}
           </div>
          }
        </div>
      )}
        <button style={btnSecondary} onClick={onClose}>Cancel</button>
        <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : customer ? 'Save Changes' : 'Add Customer'}</button>
      </div>
    </Modal>
  );
}

// ── Vendors Tab ────────────────────────────────────────────────────────────────
function VendorsTab() {
  const [vendors, setVendors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    axios.get('/api/vendors-list').then(r => { setVendors(r.data.vendors || []); setStats(r.data.stats); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sc = { active: C.green, inactive: C.muted, review: C.amber };
  const tc = { 'Net-15': C.blue, 'Net-30': C.purple, 'Prepaid': C.amber };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    await axios.delete(`/api/vendors-list/${id}`).catch(() => {});
    load();
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px', marginBottom: '20px' }}>
        <StatCard label="Active Vendors" value={stats?.active_count || 0} color={C.green} />
        <StatCard label="Monthly Spend" value={`$${parseFloat(stats?.total_monthly_spend || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} color={C.amber} />
        <StatCard label="Under Review" value={vendors.filter(v => v.status === 'review').length} color={C.orange} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
        <button style={btnPrimary} onClick={() => setModal('add')}>+ Add Vendor</button>
      </div>
      {loading && <div style={{ color: C.muted }}>Loading...</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '14px' }}>
        {vendors.map((v, i) => (
          <div key={i} style={{ ...card(false), cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div><div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '3px' }}>{v.company_name}</div><div style={{ fontSize: '11.5px', color: C.muted }}>{v.category}</div></div>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={pill(tc[v.payment_terms] || C.muted, `${tc[v.payment_terms] || C.muted}18`)}>{v.payment_terms || '—'}</span>
                <span style={pill(sc[v.status] || C.muted, `${sc[v.status] || C.muted}18`)}>{v.status}</span>
              </div>
            </div>
            {v.rep_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: C.amber + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: C.amber }}>{v.rep_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</div>
                <div><div style={{ fontSize: '12.5px', fontWeight: 500 }}>{v.rep_name}</div><div style={{ fontSize: '11px', color: C.muted }}>{v.rep_phone || v.rep_email || 'No contact'}</div></div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: `1px solid ${C.border}`, marginTop: '4px' }}>
              <div style={{ fontSize: '11px', color: C.muted }}>{v.visit_cycle || 'Monthly'}</div>
              <div style={{ ...mono, fontSize: '14px', fontWeight: 700, color: C.amber }}>${parseFloat(v.monthly_spend || 0).toLocaleString()}<span style={{ fontSize: '10px', color: C.muted, fontWeight: 400 }}>/mo</span></div>
            </div>
            <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
              {v.rep_phone && <a href={`tel:${v.rep_phone}`} style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: '6px', background: 'rgba(62,207,142,.1)', color: C.green, fontSize: '11.5px', fontWeight: 600, textDecoration: 'none' }}>Call</a>}
              {v.rep_email && <a href={`mailto:${v.rep_email}`} style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: '6px', background: 'rgba(79,142,247,.1)', color: C.blue, fontSize: '11.5px', fontWeight: 600, textDecoration: 'none' }}>Email</a>}
              <button onClick={() => setModal(v)} style={{ flex: 1, padding: '6px', borderRadius: '6px', background: 'rgba(167,139,250,.1)', color: C.purple, fontSize: '11.5px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>Edit</button>
              <button onClick={() => handleDelete(v.id)} style={{ padding: '6px 10px', borderRadius: '6px', background: 'rgba(232,72,74,.1)', color: C.red, fontSize: '11.5px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>✕</button>
            </div>
          </div>
        ))}
      </div>
      {modal && <VendorModal vendor={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </>
  );
}

function VendorModal({ vendor, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: vendor?.company_name || '', category: vendor?.category || '',
    rep_name: vendor?.rep_name || '', rep_phone: vendor?.rep_phone || '', rep_email: vendor?.rep_email || '',
    payment_terms: vendor?.payment_terms || 'Net-30', visit_cycle: vendor?.visit_cycle || 'Monthly',
    monthly_spend: vendor?.monthly_spend || '', account_number: vendor?.account_number || '',
    website: vendor?.website || '', notes: vendor?.notes || '', status: vendor?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [savingCust, setSavingCust] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.company_name) { setError('Company name required'); return; }
    setSaving(true);
    try {
      if (vendor) await axios.put(`/api/vendors-list/${vendor.id}`, form);
      else await axios.post('/api/vendors-list', form);
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || e.message); setSaving(false); }
  };

  return (
    <Modal title={vendor ? 'Edit Vendor' : 'Add Vendor'} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Field label="Company Name *"><Input value={form.company_name} onChange={e => set('company_name', e.target.value)} /></Field>
        <Field label="Category"><Input value={form.category} onChange={e => set('category', e.target.value)} placeholder="Vapes, Glass, CBD..." /></Field>
        <Field label="Rep Name"><Input value={form.rep_name} onChange={e => set('rep_name', e.target.value)} /></Field>
        <Field label="Rep Phone"><Input value={form.rep_phone} onChange={e => set('rep_phone', e.target.value)} /></Field>
        <Field label="Rep Email"><Input value={form.rep_email} onChange={e => set('rep_email', e.target.value)} type="email" /></Field>
        <Field label="Account Number"><Input value={form.account_number} onChange={e => set('account_number', e.target.value)} /></Field>
        <Field label="Payment Terms">
          <Select value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)}>
            {['Net-15', 'Net-30', 'Net-60', 'Prepaid', 'COD'].map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Visit Cycle">
          <Select value={form.visit_cycle} onChange={e => set('visit_cycle', e.target.value)}>
            {['Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'Online Portal'].map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Monthly Spend ($)"><Input type="number" value={form.monthly_spend} onChange={e => set('monthly_spend', e.target.value)} step="0.01" min="0" /></Field>
        <Field label="Status">
          <Select value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="review">Under Review</option>
            <option value="inactive">Inactive</option>
          </Select>
        </Field>
        <Field label="Website" style={{ gridColumn: '1/-1' }}><Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://" /></Field>
      </div>
      <Field label="Notes"><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></Field>
      {error && <div style={{ color: C.red, fontSize: '12px', marginBottom: '10px' }}>⚠ {error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button style={btnSecondary} onClick={onClose}>Cancel</button>
        <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : vendor ? 'Save Changes' : 'Add Vendor'}</button>
      </div>
    </Modal>
  );
}

// ── Contacts Tab ───────────────────────────────────────────────────────────────
function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    axios.get('/api/biz-contacts').then(r => { setContacts(r.data.contacts || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const types = ['all', 'property', 'finance', 'vendor_rep', 'sales_team', 'other'];
  const tl = { property: 'Property', finance: 'Finance', vendor_rep: 'Vendor Rep', sales_team: 'Sales Team', other: 'Other' };
  const tc = { property: C.blue, finance: C.green, vendor_rep: C.amber, sales_team: C.purple, other: C.muted };
  const filtered = contacts.filter(c => filter === 'all' || c.contact_type === filter);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this contact?')) return;
    await axios.delete(`/api/biz-contacts/${id}`).catch(() => {});
    load();
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {types.map(t => <button key={t} onClick={() => setFilter(t)} style={{ padding: '6px 13px', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: filter === t ? C.amber : C.muted, background: filter === t ? 'rgba(245,166,35,0.12)' : C.panel, border: `1px solid ${filter === t ? 'rgba(245,166,35,0.3)' : C.border}` }}>{t === 'all' ? 'All' : tl[t]}</button>)}
        <button style={{ ...btnPrimary, marginLeft: 'auto' }} onClick={() => setModal('add')}>+ Add Contact</button>
      </div>
      {loading && <div style={{ color: C.muted }}>Loading...</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '14px' }}>
        {filtered.map((c, i) => {
          const cc = tc[c.contact_type] || C.muted;
          const initials = `${(c.first_name || '')[0] || ''}${(c.last_name || '')[0] || ''}`.toUpperCase();
          return (
            <div key={i} style={card(false)}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${cc}22`, border: `1px solid ${cc}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '13px', color: cc, flexShrink: 0 }}>{initials}</div>
                <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: '14px' }}>{c.first_name} {c.last_name}</div><div style={{ fontSize: '11.5px', color: C.muted }}>{c.role}{c.company ? ` · ${c.company}` : ''}</div></div>
                <span style={pill(cc, `${cc}18`)}>{tl[c.contact_type] || c.contact_type}</span>
              </div>
              {c.monthly_rent && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: `1px solid ${C.border}` }}><span style={{ fontSize: '12px', color: C.muted }}>Monthly Rent</span><span style={{ ...mono, fontSize: '13px', color: C.amber, fontWeight: 600 }}>${parseFloat(c.monthly_rent).toLocaleString()}</span></div>}
              {c.lease_renewal_date && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '12px', color: C.muted }}>Lease Renewal</span><span style={{ fontSize: '12px' }}>{new Date(c.lease_renewal_date).toLocaleDateString()}</span></div>}
              {c.notes && <div style={{ fontSize: '11.5px', color: C.muted, marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${C.border}` }}>{c.notes}</div>}
              <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                {c.phone && <a href={`tel:${c.phone}`} style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: '6px', background: 'rgba(62,207,142,.1)', color: C.green, fontSize: '11.5px', fontWeight: 600, textDecoration: 'none' }}>{c.phone}</a>}
                {c.email && <a href={`mailto:${c.email}`} style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: '6px', background: 'rgba(79,142,247,.1)', color: C.blue, fontSize: '11.5px', fontWeight: 600, textDecoration: 'none' }}>Email</a>}
                <button onClick={() => setModal(c)} style={{ padding: '6px 10px', borderRadius: '6px', background: 'rgba(167,139,250,.1)', color: C.purple, fontSize: '11.5px', border: 'none', cursor: 'pointer' }}>Edit</button>
                <button onClick={() => handleDelete(c.id)} style={{ padding: '6px 10px', borderRadius: '6px', background: 'rgba(232,72,74,.1)', color: C.red, fontSize: '11.5px', border: 'none', cursor: 'pointer' }}>✕</button>
              </div>
            </div>
          );
        })}
      </div>
      {modal && <ContactModal contact={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </>
  );
}

function ContactModal({ contact, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: contact?.first_name || '', last_name: contact?.last_name || '',
    company: contact?.company || '', role: contact?.role || '',
    contact_type: contact?.contact_type || 'other',
    phone: contact?.phone || '', email: contact?.email || '',
    monthly_rent: contact?.monthly_rent || '', lease_renewal_date: contact?.lease_renewal_date || '',
    notes: contact?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [savingCust, setSavingCust] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.first_name) { setError('First name required'); return; }
    setSaving(true);
    try {
      if (contact) await axios.put(`/api/biz-contacts/${contact.id}`, form);
      else await axios.post('/api/biz-contacts', form);
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || e.message); setSaving(false); }
  };

  return (
    <Modal title={contact ? 'Edit Contact' : 'Add Business Contact'} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Field label="First Name *"><Input value={form.first_name} onChange={e => set('first_name', e.target.value)} /></Field>
        <Field label="Last Name"><Input value={form.last_name} onChange={e => set('last_name', e.target.value)} /></Field>
        <Field label="Company"><Input value={form.company} onChange={e => set('company', e.target.value)} /></Field>
        <Field label="Role / Title"><Input value={form.role} onChange={e => set('role', e.target.value)} placeholder="Property Manager" /></Field>
        <Field label="Contact Type">
          <Select value={form.contact_type} onChange={e => set('contact_type', e.target.value)}>
            <option value="property">Property</option>
            <option value="finance">Finance</option>
            <option value="vendor_rep">Vendor Rep</option>
            <option value="sales_team">Sales Team</option>
            <option value="other">Other</option>
          </Select>
        </Field>
        <Field label="Phone"><Input value={form.phone} onChange={e => set('phone', e.target.value)} /></Field>
        <Field label="Email"><Input value={form.email} onChange={e => set('email', e.target.value)} type="email" /></Field>
        <Field label="Monthly Rent ($)"><Input type="number" value={form.monthly_rent} onChange={e => set('monthly_rent', e.target.value)} step="0.01" /></Field>
        <Field label="Lease Renewal Date"><Input type="date" value={form.lease_renewal_date} onChange={e => set('lease_renewal_date', e.target.value)} /></Field>
      </div>
      <Field label="Notes"><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></Field>
      {error && <div style={{ color: C.red, fontSize: '12px', marginBottom: '10px' }}>⚠ {error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button style={btnSecondary} onClick={onClose}>Cancel</button>
        <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : contact ? 'Save Changes' : 'Add Contact'}</button>
      </div>
    </Modal>
  );
}

// ── CRM Tab ────────────────────────────────────────────────────────────────────
function CRMTab() {
  const [pipeline, setPipeline] = useState({ lead: [], in_talks: [], proposal: [], won: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    axios.get('/api/crm-pipeline').then(r => { setPipeline(r.data.pipeline || {}); setStats(r.data.stats); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const stages = [
    { id: 'lead', label: 'New Lead', color: C.muted },
    { id: 'in_talks', label: 'In Talks', color: C.blue },
    { id: 'proposal', label: 'Proposal Sent', color: C.amber },
    { id: 'won', label: 'Won', color: C.green },
  ];
  const tc = { wholesale: C.blue, b2b: C.purple, advertising: C.amber, marketing: C.green, software: C.orange };

  const moveStage = async (id, stage) => { await axios.put(`/api/crm-pipeline/${id}`, { stage }).catch(() => {}); load(); };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    await axios.delete(`/api/crm-pipeline/${id}`).catch(() => {});
    load();
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '20px' }}>
        <StatCard label="Active Deals" value={stats?.active_deals || 0} color={C.blue} />
        <StatCard label="Pipeline Value" value={`$${parseFloat(stats?.pipeline_value || 0).toLocaleString()}`} color={C.amber} />
        <StatCard label="Won This Month" value={stats?.won_this_month || 0} color={C.green} />
        <StatCard label="Follow-ups Due" value={stats?.follow_ups_due || 0} color={C.red} alert={stats?.follow_ups_due > 0} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
        <button style={btnPrimary} onClick={() => setModal('add')}>+ Add Deal</button>
      </div>
      {loading && <div style={{ padding: '30px', textAlign: 'center', color: C.muted }}>Loading...</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px' }}>
        {stages.map(stage => (
          <div key={stage.id}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', paddingBottom: '8px', borderBottom: `2px solid ${stage.color}44` }}>
              <div style={{ fontWeight: 700, fontSize: '12.5px', color: stage.color }}>{stage.label}</div>
              <span style={{ ...mono, fontSize: '11px', background: `${stage.color}18`, color: stage.color, padding: '2px 8px', borderRadius: '10px' }}>{(pipeline[stage.id] || []).length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(pipeline[stage.id] || []).map((deal, i) => {
                const dc = tc[deal.deal_type] || C.muted;
                const due = deal.follow_up_date && new Date(deal.follow_up_date) <= new Date() && !['won', 'lost'].includes(deal.stage);
                return (
                  <div key={i} style={{ ...card(due) }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>{deal.deal_name}</div>
                    {deal.company_name && deal.company_name !== deal.deal_name && <div style={{ fontSize: '11.5px', color: C.muted, marginBottom: '6px' }}>{deal.company_name}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={pill(dc, `${dc}18`)}>{deal.deal_type}</span>
                      <div style={{ ...mono, fontSize: '13px', color: C.amber, fontWeight: 700 }}>${parseFloat(deal.deal_value || 0).toLocaleString()}{deal.recurring && <span style={{ fontSize: '9px', color: C.muted }}>/mo</span>}</div>
                    </div>
                    {deal.contact_name && <div style={{ fontSize: '11px', color: C.muted, marginBottom: '4px' }}>👤 {deal.contact_name}</div>}
                    {deal.follow_up_date && <div style={{ fontSize: '11px', color: due ? C.red : C.muted, marginBottom: '6px' }}>{due ? '🔴' : '📅'} {new Date(deal.follow_up_date).toLocaleDateString()}</div>}
                    {deal.notes && <div style={{ fontSize: '11px', color: C.dim, marginBottom: '8px', paddingTop: '6px', borderTop: `1px solid ${C.border}` }}>{deal.notes}</div>}
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
                      {stages.filter(s => s.id !== stage.id).map(s => (
                        <button key={s.id} onClick={() => moveStage(deal.id, s.id)} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '5px', background: `${s.color}18`, color: s.color, border: 'none', cursor: 'pointer', fontWeight: 600 }}>→ {s.label}</button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => setModal(deal)} style={{ flex: 1, fontSize: '10px', padding: '4px', borderRadius: '5px', background: 'rgba(79,142,247,.1)', color: C.blue, border: 'none', cursor: 'pointer' }}>Edit</button>
                      <button onClick={() => handleDelete(deal.id)} style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '5px', background: 'rgba(232,72,74,.1)', color: C.red, border: 'none', cursor: 'pointer' }}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {modal && <DealModal deal={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </>
  );
}

function DealModal({ deal, onClose, onSaved }) {
  const [form, setForm] = useState({
    deal_name: deal?.deal_name || '', company_name: deal?.company_name || '',
    contact_name: deal?.contact_name || '', contact_phone: deal?.contact_phone || '', contact_email: deal?.contact_email || '',
    deal_type: deal?.deal_type || 'wholesale', stage: deal?.stage || 'lead',
    deal_value: deal?.deal_value || '', recurring: deal?.recurring || false,
    recurring_period: deal?.recurring_period || 'monthly',
    follow_up_date: deal?.follow_up_date || '', notes: deal?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [savingCust, setSavingCust] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.deal_name) { setError('Deal name required'); return; }
    setSaving(true);
    try {
      if (deal) await axios.put(`/api/crm-pipeline/${deal.id}`, form);
      else await axios.post('/api/crm-pipeline', form);
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || e.message); setSaving(false); }
  };

  return (
    <Modal title={deal ? 'Edit Deal' : 'Add Deal'} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Field label="Deal Name *"><Input value={form.deal_name} onChange={e => set('deal_name', e.target.value)} /></Field>
        <Field label="Company"><Input value={form.company_name} onChange={e => set('company_name', e.target.value)} /></Field>
        <Field label="Contact Name"><Input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} /></Field>
        <Field label="Contact Phone"><Input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} /></Field>
        <Field label="Contact Email"><Input value={form.contact_email} onChange={e => set('contact_email', e.target.value)} type="email" /></Field>
        <Field label="Deal Type">
          <Select value={form.deal_type} onChange={e => set('deal_type', e.target.value)}>
            {['wholesale', 'b2b', 'advertising', 'marketing', 'software'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </Select>
        </Field>
        <Field label="Stage">
          <Select value={form.stage} onChange={e => set('stage', e.target.value)}>
            <option value="lead">New Lead</option>
            <option value="in_talks">In Talks</option>
            <option value="proposal">Proposal Sent</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </Select>
        </Field>
        <Field label="Deal Value ($)"><Input type="number" value={form.deal_value} onChange={e => set('deal_value', e.target.value)} step="0.01" min="0" /></Field>
        <Field label="Follow-up Date"><Input type="date" value={form.follow_up_date} onChange={e => set('follow_up_date', e.target.value)} /></Field>
        <Field label="Recurring">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
            <input type="checkbox" id="rec" checked={form.recurring} onChange={e => set('recurring', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
            <label htmlFor="rec" style={{ fontSize: '13px', cursor: 'pointer' }}>Recurring Deal</label>
          </div>
        </Field>
      </div>
      <Field label="Notes"><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></Field>
      {error && <div style={{ color: C.red, fontSize: '12px', marginBottom: '10px' }}>⚠ {error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button style={btnSecondary} onClick={onClose}>Cancel</button>
        <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : deal ? 'Save Changes' : 'Add Deal'}</button>
      </div>
    </Modal>
  );
}

// ── Staff Commissions Tab ──────────────────────────────────────────────────────
function StaffCommissionsTab() {
  const { tenant } = useTenant();
  const tid = tenant?.id;
  const [staff, setStaff] = useState([]);
  const [sales, setSales] = useState([]);
  const [rates, setRates] = useState({});
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [editRate, setEditRate] = useState(null);
  const [rateInput, setRateInput] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [expandedSale, setExpandedSale] = useState(null);

  useEffect(() => {
    if (!tid) return;
    Promise.all([
      axios.get('/api/staff'),
      axios.get('/api/smokeshop-sales?limit=2000'),
    ]).then(([sr, salr]) => {
      setStaff(sr.data.staff || []);
      setSales(salr.data.sales || []);
      const saved = JSON.parse(localStorage.getItem('commission_rates_' + tid) || '{}');
      setRates(saved);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [tid]);

  function saveRate(staffName) {
    const updated = { ...rates, [staffName]: parseFloat(rateInput) || 0 };
    setRates(updated);
    localStorage.setItem('commission_rates_' + tid, JSON.stringify(updated));
    setEditRate(null);
  }

  function filtered() {
    const now = new Date();
    return sales.filter(s => {
      const d = new Date(s.created_at);
      if (period === 'week') { const w = new Date(now); w.setDate(now.getDate()-7); return d >= w; }
      if (period === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      if (period === 'year') return d.getFullYear() === now.getFullYear();
      return true;
    });
  }

  const periodSales = filtered();

  const staffStats = staff.map(s => {
    const name = s.name || (s.first_name + ' ' + (s.last_name || '')).trim();
    const mySales = periodSales.filter(sale => sale.staff_name === name);
    const totalSales = mySales.reduce((sum, sale) => sum + parseFloat(sale.total || 0), 0);
    const txCount = mySales.length;
    const avgSale = txCount > 0 ? totalSales / txCount : 0;
    const rate = rates[name] || 0;
    const commission = totalSales * (rate / 100);
    return { name, totalSales, txCount, avgSale, rate, commission, id: s.id, sales: mySales };
  }).sort((a, b) => b.totalSales - a.totalSales);

  const totalCommissions = staffStats.reduce((s, st) => s + st.commission, 0);
  const totalRevenue = staffStats.reduce((s, st) => s + st.totalSales, 0);
  const medal = ['🥇','🥈','🥉'];

  function formatDateTime(dt) {
    if (!dt) return '—';
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const payColors = { cash: C.green, card: C.blue, venmo: C.purple, zelle: C.amber };

  // ── STAFF DETAIL VIEW ──
  if (selectedStaff) {
    const s = staffStats.find(st => st.name === selectedStaff);
    if (!s) return null;
    return (
      <div style={{ padding: '0 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setSelectedStaff(null)} style={{ background: 'none', border: '1px solid ' + C.border, color: C.muted, cursor: 'pointer', fontSize: 13, padding: '5px 12px', borderRadius: 8, fontWeight: 600 }}>← Back</button>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{s.name}</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {['week','month','year','all'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 11, background: period === p ? C.amber : C.panel, color: period === p ? '#000' : C.muted }}>{p.charAt(0).toUpperCase()+p.slice(1)}</button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
          {[
            ['Total Sales', '$' + s.totalSales.toFixed(2), C.green],
            ['Transactions', s.txCount, C.blue],
            ['Avg Sale', '$' + s.avgSale.toFixed(2), C.text],
            ['Commission (' + s.rate + '%)', '$' + s.commission.toFixed(2), C.amber],
          ].map(([label, val, color]) => (
            <div key={label} style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Transactions table */}
        <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid ' + C.border, fontWeight: 700, fontSize: 13 }}>
            All Transactions ({s.txCount})
          </div>
          {s.sales.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>No transactions this {period}</div>
          ) : s.sales.map((sale, i) => {
            const items = (() => { try { return JSON.parse(sale.line_items || '[]'); } catch { return []; } })();
            const isExpanded = expandedSale === sale.id;
            return (
              <div key={sale.id || i} style={{ borderBottom: '1px solid #1a1a22' }}>
                <div onClick={() => setExpandedSale(isExpanded ? null : sale.id)}
                  style={{ display: 'flex', alignItems: 'center', padding: '12px 18px', gap: 12, cursor: 'pointer', background: isExpanded ? 'rgba(245,166,35,0.04)' : 'transparent' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{sale.customer_name || 'Walk-in'}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{formatDateTime(sale.created_at)}</div>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: (payColors[sale.payment_method] || C.muted) + '20', color: payColors[sale.payment_method] || C.muted, textTransform: 'capitalize' }}>
                    {sale.payment_method}
                  </span>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.amber, minWidth: 70, textAlign: 'right' }}>${parseFloat(sale.total).toFixed(2)}</div>
                  <div style={{ color: C.muted, fontSize: 12 }}>{isExpanded ? '▲' : '▼'}</div>
                </div>
                {isExpanded && items.length > 0 && (
                  <div style={{ padding: '0 18px 14px', background: 'rgba(0,0,0,0.2)' }}>
                    {items.map((item, j) => (
                      <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', borderBottom: '1px solid #1a1a22', color: C.muted }}>
                        <span>{item.name} <span style={{ color: C.dim }}>×{item.qty}</span></span>
                        <span style={{ color: C.text }}>${(parseFloat(item.price || 0) * parseInt(item.qty || 1)).toFixed(2)}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '8px 0 0', color: C.muted }}>
                      <span>Commission ({s.rate}%)</span>
                      <span style={{ color: C.amber, fontWeight: 700 }}>${(parseFloat(sale.total) * s.rate / 100).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── LEADERBOARD VIEW ──
  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>👥 Staff & Commissions</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>Click a staff member to see full transaction history</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['week','month','year','all'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, background: period === p ? C.amber : C.panel, color: period === p ? '#000' : C.muted }}>
              {p.charAt(0).toUpperCase()+p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {[['Total Revenue', '$' + totalRevenue.toFixed(2), C.green], ['Total Commissions', '$' + totalCommissions.toFixed(2), C.amber], ['Staff Members', staffStats.length, C.blue]].map(([label, val, color]) => (
          <div key={label} style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, padding: '18px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>🏆 Leaderboard</span>
          <span style={{ fontSize: 11, color: C.muted }}>Click row to view transactions · Click % to set rate</span>
        </div>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Loading...</div>
        : staffStats.map((s, i) => (
          <div key={s.id} onClick={() => setSelectedStaff(s.name)}
            style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #1a1a22', gap: 14, cursor: 'pointer', transition: 'background .15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,166,35,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{ fontSize: 20, width: 32, textAlign: 'center', flexShrink: 0 }}>{medal[i] || (i+1)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{s.name}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{s.txCount} transactions · avg ${s.avgSale.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'right', minWidth: 90 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.green }}>${s.totalSales.toFixed(2)}</div>
              <div style={{ fontSize: 10, color: C.muted }}>in sales</div>
            </div>
            <div onClick={e => { e.stopPropagation(); setEditRate(s.name); setRateInput(String(s.rate)); }} style={{ textAlign: 'center', minWidth: 80 }}>
              {editRate === s.name ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
                  <input type="number" value={rateInput} onChange={e => setRateInput(e.target.value)}
                    style={{ width: 50, padding: '4px 6px', background: '#1a1a22', border: '1px solid ' + C.border, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }}
                    autoFocus onKeyDown={e => e.key === 'Enter' && saveRate(s.name)} />
                  <span style={{ color: C.muted, fontSize: 12 }}>%</span>
                  <button onClick={e => { e.stopPropagation(); saveRate(s.name); }} style={{ padding: '3px 8px', background: C.amber, color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 11 }}>✓</button>
                </div>
              ) : (
                <span style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 8, padding: '4px 10px', color: C.amber, fontWeight: 700, fontSize: 13 }}>{s.rate}%</span>
              )}
            </div>
            <div style={{ textAlign: 'right', minWidth: 90 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.amber }}>${s.commission.toFixed(2)}</div>
              <div style={{ fontSize: 10, color: C.muted }}>commission</div>
            </div>
            <div style={{ color: C.muted, fontSize: 12 }}>→</div>
          </div>
        ))}
      </div>
    </div>
  );
}
// ── Import Tab wrapper ─────────────────────────────────────────────────────────
function ImportTab() {
  return (
    <div style={{ padding: '0 4px' }}>
      <Import />
    </div>
  );
}
